import { z } from "zod";
import type { LodgingBuildingType } from "./accommodation";
import type { AcCardStatus } from "./accreditation";
import type { AccreditationOrgRef, PersonRoleRefs } from "./accreditation-records";
import { PAGE_SIZE_MAX } from "./pagination";
import { type EntryPointRef, type FlightStatus, TRAVEL_DIRECTIONS, type TravelDirection } from "./travel";

/**
 * Transportation (M10): transfers that follow travel, venue trips, dispatch,
 * the delay cascade, the fleet and provider logins. Wire shapes only
 * (snake_case). Plan: `~/.claude/plans/m10-transport/PLAN.md`.
 *
 * The database CHECKs (migration `0024_transport`) are generated from the
 * tuples, ranges and patterns below; a parity test holds them equal. Rename
 * nothing here without the migration.
 *
 * Rules both sides lean on:
 *   · Only OPEN / CANCELLED / REJECTED are STORED (`transport_requests.state`).
 *     What people see (`TRANSPORT_DISPLAY_STATUSES`) is derived in SQL from
 *     the allocations — `deriveTransportDisplayStatus` is the same rule for
 *     tests and mocks, never a second source.
 *   · Suggestions are computed, never stored: an APPROVED card's leg with
 *     `needs_transport`, a known `expected_at` and no live transfer IS one.
 *   · Every place has a cluster: drive time and the dispatch cluster are
 *     always known. No free-text destination.
 *   · Double-booking is impossible: a gist EXCLUDE on the live allocations
 *     (`TRANSPORT_ALLOCATION_LIVE_STATES`) of a vehicle and of a driver.
 *   · NOC privacy (§0b Q3): a NOC's view of a transfer carries ONLY its own
 *     passengers and its own count — never the total, the seats left, other
 *     organisations or a merged group's other flights. The NOC read builds
 *     its counts from its own passengers in SQL (`TransportNocTrip`).
 *   · A provider login sees the trips on its vehicles with passenger COUNTS
 *     and the group lead's contact — no name list (§0b Q1, `TransportProviderTrip`).
 */

// ── Vocabulary ──────────────────────────────────────────────────────────────

/**
 * Where a transfer comes from:
 *   ARR    arrival: entry point → destination; follows the passengers' arrival legs
 *   DEP    departure: origin → entry point; follows the departure legs
 *   ADHOC  a venue trip (NOC or host), with an optional return pickup
 */
export const TRANSPORT_REQUEST_SOURCES = ["ARR", "DEP", "ADHOC"] as const;
export type TransportRequestSource = (typeof TRANSPORT_REQUEST_SOURCES)[number];

/** The STORED state of a request (`transport_requests.state`, CHECK). Everything else is derived. */
export const TRANSPORT_REQUEST_STATES = ["OPEN", "CANCELLED", "REJECTED"] as const;
export type TransportRequestState = (typeof TRANSPORT_REQUEST_STATES)[number];

export interface TransportRequestTransition {
	from: TransportRequestState;
	to: TransportRequestState;
	/** The `POST /transport/requests/bulk/status` action that takes this edge. */
	action: "cancel" | "reject";
}

/**
 * The request state graph. The BEFORE UPDATE trigger
 * `fn_transport_request_state_guard` refuses any other move — psql and AI
 * agents included — with 23514 `ck_transport_requests_state_transition`
 * (→ INVALID_STATUS_TRANSITION). `tests/unit/transport-state-guards.test.ts`
 * holds the trigger and this list equal. The trigger also refuses REJECTED
 * while a live allocation exists, and CANCELLED while one is EN_ROUTE.
 */
export const TRANSPORT_REQUEST_TRANSITIONS: readonly TransportRequestTransition[] = [
	{ from: "OPEN", to: "CANCELLED", action: "cancel" },
	{ from: "OPEN", to: "REJECTED", action: "reject" },
];

/**
 * The status shown (list chips, detail header), derived in SQL:
 *   PENDING    OPEN, no live allocation                    ("Pending driver")
 *   SHORT      OPEN, all ASSIGNED, fewer seats than people ("Partly assigned")
 *   ASSIGNED   OPEN, all ASSIGNED, everyone seated         ("Driver assigned")
 *   EN_ROUTE   OPEN, at least one EN_ROUTE                 ("En route")
 *   PARTIAL    OPEN, some DONE, not all                    ("Partly completed")
 *   COMPLETED  OPEN, every live allocation DONE            ("Trip completed")
 *   CANCELLED / REJECTED  the stored state
 */
export const TRANSPORT_DISPLAY_STATUSES = ["PENDING", "SHORT", "ASSIGNED", "EN_ROUTE", "PARTIAL", "COMPLETED", "CANCELLED", "REJECTED"] as const;
export type TransportDisplayStatus = (typeof TRANSPORT_DISPLAY_STATUSES)[number];

/**
 * What the derived status is computed from (one LATERAL aggregate over the
 * request's allocations, CANCELLED ones excluded, plus its seat count).
 */
export interface TransportStatusInputs {
	state: TransportRequestState;
	/** Allocations that are not CANCELLED. */
	allocations: number;
	en_route: number;
	done: number;
	/** Σ pax of the ASSIGNED / EN_ROUTE / DONE allocations. */
	seats_assigned: number;
	/** Named passengers (not released) + `unnamed_count`. */
	seats_needed: number;
}

/**
 * TRANSPORT_DISPLAY_STATUSES, first match wins. The SQL CASE
 * (`displayStatusSql` in apps/api/src/db/queries/transport-rules.ts) is the
 * same order; this copy serves tests and mocks, never a second decision.
 */
export function deriveTransportDisplayStatus(i: TransportStatusInputs): TransportDisplayStatus {
	if (i.state !== "OPEN") return i.state;
	if (i.allocations === 0) return "PENDING";
	if (i.done === i.allocations) return "COMPLETED";
	if (i.done > 0) return "PARTIAL";
	if (i.en_route > 0) return "EN_ROUTE";
	return i.seats_assigned < i.seats_needed ? "SHORT" : "ASSIGNED";
}

/** An allocation: one vehicle + one driver + a number of seats on one request. */
export const TRANSPORT_ALLOCATION_STATES = ["ASSIGNED", "EN_ROUTE", "DONE", "CANCELLED"] as const;
export type TransportAllocationState = (typeof TRANSPORT_ALLOCATION_STATES)[number];

/** The states that hold a vehicle and a driver: the WHERE of both gist EXCLUDE constraints. */
export const TRANSPORT_ALLOCATION_LIVE_STATES = ["ASSIGNED", "EN_ROUTE"] as const satisfies readonly TransportAllocationState[];
/** A trip has STARTED once one of its allocations is in these states: allocations lock (TRIP_LOCKED), a NOC can no longer cancel, the cascade leaves it alone. */
export const TRANSPORT_ALLOCATION_STARTED_STATES = ["EN_ROUTE", "DONE"] as const satisfies readonly TransportAllocationState[];

export interface TransportAllocationTransition {
	from: TransportAllocationState;
	to: TransportAllocationState;
	/** `POST /transport/allocations/:id/{start,arrive,cancel}`; `null` = only the system takes it (editor, cascade, request closed). */
	action: "start" | "arrive" | "cancel" | null;
}

/**
 * The allocation state graph (`fn_transport_allocation_state_guard`, 23514
 * `ck_transport_allocations_state_transition`). Start stamps `started_at`,
 * Arrived stamps `arrived_at`. Held equal to the trigger by
 * `tests/unit/transport-state-guards.test.ts`.
 */
export const TRANSPORT_ALLOCATION_TRANSITIONS: readonly TransportAllocationTransition[] = [
	{ from: "ASSIGNED", to: "EN_ROUTE", action: "start" },
	{ from: "EN_ROUTE", to: "DONE", action: "arrive" },
	{ from: "ASSIGNED", to: "CANCELLED", action: "cancel" },
	{ from: "EN_ROUTE", to: "CANCELLED", action: "cancel" },
];

/**
 * Why an allocation is CANCELLED (CHECK `(state = 'CANCELLED') = (cancel_reason IS NOT NULL)`):
 *   EDITED               the dispatcher replaced the allocations
 *   REQUEST_CANCELLED    the request was cancelled (trigger `fn_transport_request_closed`)
 *   DELAY_CLASH          a delay moved the trip onto another trip of the vehicle or driver
 *   VEHICLE_UNAVAILABLE  cancelled by hand: breakdown, accident, no-show
 */
export const TRANSPORT_ALLOCATION_CANCEL_REASONS = ["EDITED", "REQUEST_CANCELLED", "DELAY_CLASH", "VEHICLE_UNAVAILABLE"] as const;
export type TransportAllocationCancelReason = (typeof TRANSPORT_ALLOCATION_CANCEL_REASONS)[number];

/** CAR = "Private car". */
export const VEHICLE_TYPES = ["CAR", "VAN", "BUS"] as const;
export type VehicleType = (typeof VEHICLE_TYPES)[number];

/** Passenger seats per vehicle type, both ends included (CHECK on `vehicles.seats` by type). */
export const VEHICLE_SEATS = {
	CAR: { min: 2, max: 7 },
	VAN: { min: 6, max: 15 },
	BUS: { min: 16, max: 60 },
} as const satisfies Record<VehicleType, { min: number; max: number }>;

export const vehicleSeatsFit = (type: VehicleType, seats: number): boolean =>
	Number.isInteger(seats) && seats >= VEHICLE_SEATS[type].min && seats <= VEHICLE_SEATS[type].max;

/** RETIRED replaces delete for a vehicle that has trips (allocations RESTRICT it). */
export const VEHICLE_STATUSES = ["ACTIVE", "MAINTENANCE", "RETIRED"] as const;
export type VehicleStatus = (typeof VEHICLE_STATUSES)[number];

/**
 * The kinds of place a trip can start or end at — one FK column per kind on
 * `transport_requests` (`from_venue_id`, `from_entry_point_id`,
 * `from_place_id`, same for `to_*`). M11 adds BUILDING.
 *   VENUE        a competition / training venue that has a cluster
 *   ENTRY_POINT  an airport, CIQ or ferry terminal (M9)
 *   PLACE        `transport_places`: hotels before M11, embassies, training grounds
 */
export const PLACE_KINDS = ["VENUE", "ENTRY_POINT", "PLACE"] as const;
export type PlaceKind = (typeof PLACE_KINDS)[number];

/**
 * A driver's status now (Fleet chips, Overview tile), first match wins:
 * ON_TRIP (an EN_ROUTE allocation) > REST (rest weekday today, games time
 * zone) > ON_DUTY (inside the shift now) > OFF_DUTY.
 */
export const DRIVER_NOW_STATUSES = ["ON_TRIP", "REST", "ON_DUTY", "OFF_DUTY"] as const;
export type DriverNowStatus = (typeof DRIVER_NOW_STATUSES)[number];

/**
 * `transport_check_allocations(id, rows)` per-row codes (her `check()`), in
 * the order they are tested:
 *   VEHICLE_REQUIRED · DRIVER_REQUIRED · DRIVER_INACTIVE (`is_active = false`)
 *   PAX_INVALID (not a whole number ≥ 1)
 *   OVER_CAPACITY (pax > seats) · VEHICLE_INACTIVE (MAINTENANCE / RETIRED)
 *   VEHICLE_DUPLICATE (twice in this trip) · VEHICLE_BUSY (`ref` = the other trip)
 *   DRIVER_NOT_CERTIFIED (type) · DRIVER_REST_DAY · DRIVER_OFF_SHIFT
 *   DRIVER_DUPLICATE · DRIVER_BUSY (`ref`)
 */
export const TRANSPORT_ROW_CHECK_CODES = [
	"VEHICLE_REQUIRED",
	"DRIVER_REQUIRED",
	"DRIVER_INACTIVE",
	"PAX_INVALID",
	"OVER_CAPACITY",
	"VEHICLE_INACTIVE",
	"VEHICLE_DUPLICATE",
	"VEHICLE_BUSY",
	"DRIVER_NOT_CERTIFIED",
	"DRIVER_REST_DAY",
	"DRIVER_OFF_SHIFT",
	"DRIVER_DUPLICATE",
	"DRIVER_BUSY",
] as const;
export type TransportRowCheckCode = (typeof TRANSPORT_ROW_CHECK_CODES)[number];

/**
 * Whole-trip codes:
 *   NO_ROWS        no vehicle at all
 *   SEATS_SHORT    `n` people still without a seat (accepted with `accept_partial`)
 *   SEATS_OVER     `n` seats more than people
 *   RAMP_REQUIRED  ramp seats < wheelchair users
 *   OTHER_CLUSTER  a vehicle or driver from another cluster — a WARNING, never blocks
 */
export const TRANSPORT_TOTAL_CHECK_CODES = ["NO_ROWS", "SEATS_SHORT", "SEATS_OVER", "RAMP_REQUIRED", "OTHER_CLUSTER"] as const;
export type TransportTotalCheckCode = (typeof TRANSPORT_TOTAL_CHECK_CODES)[number];

/** Codes that never block a save. */
export const TRANSPORT_CHECK_WARNINGS = ["OTHER_CLUSTER"] as const satisfies readonly TransportTotalCheckCode[];

/** Every code `transport_check_allocations` may return (the SQL function's output column is checked against this in tests). */
export const TRANSPORT_CHECK_CODES = [...TRANSPORT_ROW_CHECK_CODES, ...TRANSPORT_TOTAL_CHECK_CODES] as const;
export type TransportCheckCode = (typeof TRANSPORT_CHECK_CODES)[number];

/**
 * Why `transport_suggest` came up short:
 *   NO_DRIVER_ON_SHIFT  free vehicles remain, but no certified driver on shift for them
 *   NO_VEHICLE_FREE     not enough free vehicles in the cluster at that time
 *   NO_RAMP_VEHICLE     everyone seated, but no free ramp vehicle with a driver for the wheelchair users
 */
export const TRANSPORT_SUGGEST_WHY = ["NO_DRIVER_ON_SHIFT", "NO_VEHICLE_FREE", "NO_RAMP_VEHICLE"] as const;
export type TransportSuggestWhy = (typeof TRANSPORT_SUGGEST_WHY)[number];

/**
 * `transport_auto_assign` outcome per request:
 *   ASSIGNED  everyone seated
 *   PARTIAL   some seated, kept because `accept_partial` (the trip shows SHORT)
 *   SKIPPED   nothing written; `reason` says why
 */
export const TRANSPORT_AUTO_ASSIGN_OUTCOMES = ["ASSIGNED", "PARTIAL", "SKIPPED"] as const;
export type TransportAutoAssignOutcome = (typeof TRANSPORT_AUTO_ASSIGN_OUTCOMES)[number];

/**
 * Why auto-assign skipped (or only partly filled) a request:
 *   NOT_OPEN           cancelled or rejected
 *   ALREADY_ASSIGNED   it has live allocations already
 *   PICKUP_PASSED      pickup is in the past: "Check with the team"
 *   NO_PASSENGERS      nobody (named or unnamed) to carry
 *   NO_DRIVER_ON_SHIFT / NO_VEHICLE_FREE / NO_RAMP_VEHICLE  `transport_suggest`'s why
 *   CONFLICT           another writer took a vehicle or driver meanwhile (23P01)
 *   NOT_DISPATCHABLE   the caller may not dispatch it (scope); filtered before the function
 */
export const TRANSPORT_AUTO_ASSIGN_REASONS = [
	"NOT_OPEN",
	"ALREADY_ASSIGNED",
	"PICKUP_PASSED",
	"NO_PASSENGERS",
	...TRANSPORT_SUGGEST_WHY,
	"CONFLICT",
	"NOT_DISPATCHABLE",
] as const;
export type TransportAutoAssignReason = (typeof TRANSPORT_AUTO_ASSIGN_REASONS)[number];

/**
 * Why `POST /transport/transfers` left a leg out (`skipped[]`):
 *   on_transfer    already on a live transfer (`ref` = its trip)
 *   not_eligible   the card is not APPROVED, the leg no longer needs transport,
 *                  is the other direction, or has no expected time
 *   not_in_scope   not writable for the caller, or does not exist
 */
export const TRANSPORT_TRANSFER_SKIP_REASONS = ["on_transfer", "not_eligible", "not_in_scope"] as const;
export type TransportTransferSkipReason = (typeof TRANSPORT_TRANSFER_SKIP_REASONS)[number];

/**
 * Why `POST /transport/requests/bulk/status` left a request alone:
 *   not_in_scope  not visible / writable for the caller, or does not exist
 *   not_open      already CANCELLED or REJECTED
 *   in_progress   a vehicle is on the road or done (cancel), or a driver is assigned (reject: cancel it instead)
 */
export const TRANSPORT_BULK_SKIP_REASONS = ["not_in_scope", "not_open", "in_progress"] as const;
export type TransportBulkSkipReason = (typeof TRANSPORT_BULK_SKIP_REASONS)[number];

/**
 * The list tabs:
 *   upcoming  pickup ≥ now, ascending — PLUS trips still on the road (a vehicle EN_ROUTE, or PARTIAL) whose pickup has passed
 *   past      pickup < now and nothing on the road, descending
 *   day       every trip picking up on `date` (required), ascending, whatever its state (the provider's day view)
 */
export const TRANSPORT_REQUEST_TABS = ["upcoming", "past", "day"] as const;
export type TransportRequestTab = (typeof TRANSPORT_REQUEST_TABS)[number];

/** `POST /transport/requests/bulk/status` actions (the edge each takes is in TRANSPORT_REQUEST_TRANSITIONS). */
export const TRANSPORT_BULK_ACTIONS = ["cancel", "reject"] as const;
export type TransportBulkAction = (typeof TRANSPORT_BULK_ACTIONS)[number];

/**
 * Overview "Needs attention" kinds (`GET /transport/summary`):
 *   REDISPATCH         `needs_redispatch`: a delay released an allocation
 *   OVERDUE            PENDING with pickup passed
 *   RECHECK            ASSIGNED, future, the driver is now off shift / on a rest day after a move (Q4: kept, flagged)
 *   FLIGHT_CANCELLED   a linked leg's flight is CANCELLED
 *   NO_PASSENGERS      OPEN, nobody left on it (all detached / released)
 *   RAMP_NEEDED        PENDING with wheelchair users
 *   MAINTENANCE_BOOKED a vehicle in MAINTENANCE still holds upcoming allocations
 */
export const TRANSPORT_ATTENTION_KINDS = ["REDISPATCH", "OVERDUE", "RECHECK", "FLIGHT_CANCELLED", "NO_PASSENGERS", "RAMP_NEEDED", "MAINTENANCE_BOOKED"] as const;
export type TransportAttentionKind = (typeof TRANSPORT_ATTENTION_KINDS)[number];

// ── Limits ──────────────────────────────────────────────────────────────────

/**
 * `GET /transport/suggestions`: `from`..`to` spans at most this many days,
 * both ends included. ONE day (lead, 2026-10-14): the Overview works per day,
 * and a peak day is ~60 ms at the 60k fixture (3 days were 120 ms+).
 */
export const TRANSPORT_SUGGESTION_RANGE_DAYS_MAX = 1;
/** `GET /transport/suggestions` answers at most this many groups, then `truncated: true` (a guard, not a page). */
export const TRANSPORT_SUGGESTIONS_LIMIT = 500;
/** Organisations named per suggestion; the rest are counted in `organizations_more`. */
export const TRANSPORT_SUGGESTION_ORGS_MAX = 5;
/**
 * `GET /transport/suggestions/day-counts`: the widest range one call may
 * count (the day chips show a week; paging moves a week), both ends included.
 */
export const TRANSPORT_SUGGESTION_DAY_COUNTS_RANGE_DAYS_MAX = 14;
/** `GET /transport/suggestions` names at most this many WAITING legs (`waiting.items`); `waiting.total` counts them all. */
export const TRANSPORT_SUGGESTION_WAITING_LIMIT = 100;
/** `POST /transport/transfers`: groups per call. */
export const TRANSPORT_TRANSFER_GROUPS_MAX = 50;
/** Legs per group (`POST /transport/transfers`), per suggestion (`leg_ids`), and per dismiss call. */
export const TRANSPORT_TRANSFER_LEGS_MAX = 500;
/** `POST /transport/requests/bulk/status`: requests per call. */
export const TRANSPORT_BULK_MAX = 200;
/** `POST /transport/auto-assign`: requests per call. */
export const TRANSPORT_AUTO_ASSIGN_MAX = 200;
/** `PUT /transport/requests/:id/allocations`: vehicles on one trip. */
export const TRANSPORT_ALLOCATION_ROWS_MAX = 20;
/** Named passengers on one request (create / `PUT …/passengers`). */
export const TRANSPORT_PASSENGERS_MAX = 200;
/** People typed as a count (escorts, a NOC's head count). */
export const TRANSPORT_UNNAMED_MAX = 500;
export const TRANSPORT_BAGS_MAX = 2000;
/** `transport_suggest` picks at most this many vehicles. */
export const TRANSPORT_SUGGEST_PICKS_MAX = 40;
/** `GET /transport/drivers/:id/week`: allocations returned. */
export const TRANSPORT_DRIVER_WEEK_LIMIT = 500;
/** `GET /transport/requests` default page size (max PAGE_SIZE_MAX). */
export const TRANSPORT_REQUESTS_PAGE_SIZE_DEFAULT = 25;
/** Fleet, places, clusters, providers: short admin lists, offset pages. */
export const TRANSPORT_FLEET_PAGE_SIZE_DEFAULT = 50;

/** Drive minutes: `cluster_drive_minutes.minutes`, `transport_requests.drive_minutes`, the settings default. */
export const DRIVE_MINUTES_MIN = 1;
export const DRIVE_MINUTES_MAX = 600;

/**
 * `transport_settings` — one row per games, CHECK ranges from here:
 *   default_drive_minutes  a cluster pair with no matrix entry
 *   noc_lead_hours         ORGANIZATION trips must be booked this far ahead (hard; the host is not bound)
 *   adhoc_buffer_minutes   a one-way venue trip holds the vehicle for drive + buffer
 *   group_spread_minutes   a leg moved further than this from the rest of its transfer is detached
 */
export const TRANSPORT_SETTINGS_RANGES = {
	default_drive_minutes: { min: DRIVE_MINUTES_MIN, max: DRIVE_MINUTES_MAX, default: 60 },
	noc_lead_hours: { min: 0, max: 168, default: 12 },
	adhoc_buffer_minutes: { min: 0, max: 240, default: 15 },
	group_spread_minutes: { min: 0, max: 240, default: 30 },
} as const satisfies Record<string, { min: number; max: number; default: number }>;
export type TransportSettingKey = keyof typeof TRANSPORT_SETTINGS_RANGES;

/** ISO weekday of a driver's rest day: 1 = Monday … 7 = Sunday (`drivers.rest_isodow`). */
export const ISODOW_MIN = 1;
export const ISODOW_MAX = 7;

export const CLUSTER_CODE_MAX = 8;
export const CLUSTER_NAME_MAX = 120;
export const CLUSTER_SORT_ORDER_MAX = 9999;
export const TRANSPORT_NAME_MAX = 120;
export const TRANSPORT_ADDRESS_MAX = 300;
export const TRANSPORT_REMARKS_MAX = 500;
/** `state_reason` (cancel / reject) and the audit headline a caller adds. */
export const TRANSPORT_REASON_MAX = 300;
export const TRANSPORT_PHONE_MAX = 30;
export const TRANSPORT_EMAIL_MAX = 320;
export const VEHICLE_PLATE_MAX = 20;
export const VEHICLE_MODEL_MAX = 80;

// ── Patterns ────────────────────────────────────────────────────────────────
// One copy each: the zod schemas below test against them, and the database
// CHECKs are built from them (`matchesPattern()` in apps/api/src/db/schema).
// Plain character classes only (no `\d`, no groups): `.source` is written into
// the SQL as it stands.

/** A cluster's code: `KL`, `PTJ`, `KLIA` (upper-case, 2–8). `ck_clusters_code`. */
export const CLUSTER_CODE_PATTERN = /^[A-Z0-9]{2,8}$/;
/**
 * A plate as stored: upper-case letters, digits, spaces and hyphens, starting
 * and ending with a letter or digit, ≥ 2 characters (`B1`, `WXY 1234`,
 * `PUTRAJAYA 1`); length ≤ VEHICLE_PLATE_MAX. `ck_vehicles_plate`.
 * Uniqueness is on `plate_norm` (GENERATED, `normalizePlate`).
 */
export const VEHICLE_PLATE_PATTERN = /^[A-Z0-9][A-Z0-9 -]*[A-Z0-9]$/;
/**
 * What `plate_norm` strips: everything but letters and digits. The GENERATED
 * column is `upper(regexp_replace(plate, '<source>', '', 'g'))`.
 */
export const PLATE_NORM_STRIP_PATTERN = /[^A-Za-z0-9]/;
/** A driver's phone, E.164: `+60123456789`. `ck_drivers_phone`. */
export const E164_PHONE_PATTERN = /^[+][1-9][0-9]{6,14}$/;

/** `WXY-1234`, `wxy 1234` → `WXY1234`: the key `plate_norm` is unique on. */
export const normalizePlate = (plate: string): string => plate.replace(new RegExp(PLATE_NORM_STRIP_PATTERN.source, "g"), "").toUpperCase();

// ── Formatting ──────────────────────────────────────────────────────────────

/** `TRIP-000123`: a request's `ref_no` (from `transport_request_ref_seq`) as printed everywhere. */
export const formatTripRef = (refNo: number | string): string => `TRIP-${String(refNo).padStart(6, "0")}`;

/** `TRIP-000123`, `trip-123`, `000123`, `123` → 123. Anything else → null (the search box then tries names). */
export function parseTripRefQuery(q: string): number | null {
	const m = /^(?:TRIP-?)?0*([0-9]{1,12})$/i.exec(q.trim());
	if (!m) return null;
	const n = Number(m[1]);
	return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** Kinds a contingent's default drop-off may be (`transport_org_dropoffs`: `venue_id` XOR `transport_place_id`). */
export const DROPOFF_PLACE_KINDS = ["VENUE", "PLACE"] as const satisfies readonly PlaceKind[];
export type DropoffPlaceKind = (typeof DROPOFF_PLACE_KINDS)[number];

/** `PUT /clusters/drive-minutes`: rows per call (50 clusters, both directions). */
export const CLUSTER_DRIVE_ROWS_MAX = 2500;
/** `PUT /transport/dropoffs`: contingents per call. */
export const TRANSPORT_DROPOFFS_MAX = 500;
/** A driver's usual vehicles. */
export const DRIVER_VEHICLES_MAX = 20;
/** Seats one allocation row may carry (the largest vehicle; OVER_CAPACITY catches the real seat count). */
export const TRANSPORT_PAX_MAX = VEHICLE_SEATS.BUS.max;

/** `HH:MM`, 24 h (a driver's shift, in the games' time zone). */
export const TIME_OF_DAY_PATTERN = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

// ── Messages (message-factory pattern) ──────────────────────────────────────
// English defaults, so the API and the default exports keep the English wire
// copy while the web app passes translated copy. Every string is trimmed
// BEFORE its length check (CLAUDE.md bug 5). Two interfaces, one per web
// namespace: `transport` (requests, dispatch) and `transportFleet` (fleet,
// clusters, places, drop-offs, settings, providers).

export interface TransportFormMessages {
	required: string;
	textTooLong: string;
	dateInvalid: string;
	dateTimeInvalid: string;
	rangeInvalid: string;
	rangeTooLong: string;
	idsDuplicate: string;
	nothingToChange: string;
	/** From and to are the same place. */
	samePlace: string;
	/** An arrival must start at an entry point; a departure must end at one. */
	entryPointEnd: string;
	/** A return pickup on a transfer, or one not after the pickup. */
	returnInvalid: string;
	/** A fixed pickup time and "follow the flight" in one edit. */
	timeConflict: string;
	/** Nobody to carry: no named passenger and no count. */
	passengersRequired: string;
	passengersMax: string;
	countInvalid: string;
	/** More wheelchair users than unnamed people. */
	wheelchairTooMany: string;
	minutesInvalid: string;
	rowsMax: string;
	reasonRequired: string;
}

export const enTransportFormMessages: TransportFormMessages = {
	required: "Required",
	textTooLong: "Too long",
	dateInvalid: "Enter a valid date",
	dateTimeInvalid: "Enter a valid date and time",
	rangeInvalid: "The end date must be on or after the start date",
	rangeTooLong: TRANSPORT_SUGGESTION_RANGE_DAYS_MAX === 1 ? "One day at a time" : `At most ${TRANSPORT_SUGGESTION_RANGE_DAYS_MAX} days`,
	idsDuplicate: "Listed twice",
	nothingToChange: "Nothing to change",
	samePlace: "Pick-up and drop-off are the same place",
	entryPointEnd: "An arrival starts at an entry point; a departure ends at one",
	returnInvalid: "The return pick-up must be after the pick-up, on a venue trip",
	timeConflict: "Either fix the pick-up time or follow the flight, not both",
	passengersRequired: "Add at least one passenger",
	passengersMax: `At most ${TRANSPORT_PASSENGERS_MAX} named passengers`,
	countInvalid: "Enter a whole number, 0 or more",
	wheelchairTooMany: "More wheelchair users than passengers",
	minutesInvalid: `Enter whole minutes, ${DRIVE_MINUTES_MIN} to ${DRIVE_MINUTES_MAX}`,
	rowsMax: `At most ${TRANSPORT_ALLOCATION_ROWS_MAX} vehicles`,
	reasonRequired: "Give a reason",
};

export interface TransportFleetFormMessages {
	required: string;
	textTooLong: string;
	dateInvalid: string;
	nameRequired: string;
	nothingToChange: string;
	idsDuplicate: string;
	codeInvalid: string;
	plateInvalid: string;
	/** Seats outside the vehicle type's range — `{{min}}` / `{{max}}` are filled by the caller's copy. */
	seatsInvalid: string;
	phoneInvalid: string;
	emailInvalid: string;
	vehicleTypesRequired: string;
	timeInvalid: string;
	/** Shift start equals shift end. */
	shiftEmpty: string;
	restDayInvalid: string;
	minutesInvalid: string;
	numberInvalid: string;
	sortOrderInvalid: string;
	rowsMax: string;
	/** A drive-minutes pair listed twice. (A cluster to itself is allowed: a trip inside one cluster.) */
	pairInvalid: string;
}

export const enTransportFleetFormMessages: TransportFleetFormMessages = {
	required: "Required",
	textTooLong: "Too long",
	dateInvalid: "Enter a valid date",
	nameRequired: "Enter a name",
	nothingToChange: "Nothing to change",
	idsDuplicate: "Listed twice",
	codeInvalid: "Use 2 to 8 capital letters or digits",
	plateInvalid: "Enter a plate number like WXY 1234",
	seatsInvalid: "Seats do not fit this vehicle type",
	phoneInvalid: "Enter the phone number with its country code, like +60123456789",
	emailInvalid: "Enter a valid email address",
	vehicleTypesRequired: "Choose at least one vehicle type",
	timeInvalid: "Enter a time like 07:00",
	shiftEmpty: "Set a shift with different start and end times",
	restDayInvalid: "Pick a day of the week",
	minutesInvalid: `Enter whole minutes, ${DRIVE_MINUTES_MIN} to ${DRIVE_MINUTES_MAX}`,
	numberInvalid: "Enter a whole number in range",
	sortOrderInvalid: "Enter a whole number, 0 or more",
	rowsMax: "Too many rows",
	pairInvalid: "Each pair of clusters once",
};

type TM = TransportFormMessages;
type FM = TransportFleetFormMessages;

// ── Field builders (private) ────────────────────────────────────────────────

const uuid = z.string().uuid();
const isRealDate = (v: string) => {
	const d = new Date(`${v}T00:00:00Z`);
	return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};
const dateField = (m: { dateInvalid: string }) => z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, m.dateInvalid).refine(isRealDate, m.dateInvalid);
const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
const dateTime = (m: { dateTimeInvalid: string }) => z.string().trim().datetime({ offset: true, message: m.dateTimeInvalid });
/** Optional text: trimmed, then ≤ max; blank, null or omitted → null. */
const nullableText = (m: { textTooLong: string }, max: number) =>
	z
		.string()
		.trim()
		.max(max, m.textTooLong)
		.nullable()
		.optional()
		.transform((v) => (v === undefined || v === null || v === "" ? null : v));
/** A PATCH text field: omitted leaves it alone, null or blank clears it. */
const patchText = (m: { textTooLong: string }, max: number) =>
	z
		.string()
		.trim()
		.max(max, m.textTooLong)
		.transform((v) => (v === "" ? null : v))
		.nullable()
		.optional();
const count = (m: { countInvalid: string }, max: number) => z.number({ invalid_type_error: m.countInvalid }).int(m.countInvalid).min(0, m.countInvalid).max(max, m.countInvalid);
const driveMinutes = (m: { minutesInvalid: string }) =>
	z.number({ invalid_type_error: m.minutesInvalid }).int(m.minutesInvalid).min(DRIVE_MINUTES_MIN, m.minutesInvalid).max(DRIVE_MINUTES_MAX, m.minutesInvalid);
const uniqueIds = (ids: readonly string[]) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length;
/** `"true"` / `"false"` off the query string — never `z.coerce.boolean()` (bug 2). */
const booleanParam = z.enum(["true", "false"]).transform((v) => v === "true");
/** `a,b,c` off the query string → a de-duplicated list of `values`. */
const enumList = <T extends readonly [string, ...string[]]>(values: T) =>
	z
		.string()
		.max(200)
		.transform((v) =>
			v
				.split(",")
				.map((s) => s.trim())
				.filter((s) => s.length > 0),
		)
		.pipe(z.array(z.enum(values)).min(1))
		.transform((list) => [...new Set(list)]);
const reasonText = (m: { reasonRequired: string; textTooLong: string }) => z.string({ required_error: m.reasonRequired }).trim().min(1, m.reasonRequired).max(TRANSPORT_REASON_MAX, m.textTooLong);
const searchText = z.string().trim().min(1).max(100);
const pageParams = {
	page: z.coerce.number().int().min(1).default(1),
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(TRANSPORT_FLEET_PAGE_SIZE_DEFAULT),
};
const dirParam = z.enum(["asc", "desc"]);

/** A place on a trip as the wire names it: its kind and that row's id (`venue_id` / `entry_point_id` / `transport_place_id`). */
export const placeInput = z.object({ kind: z.enum(PLACE_KINDS), id: uuid });
export type PlaceInput = z.infer<typeof placeInput>;
const samePlace = (a: PlaceInput, b: PlaceInput) => a.kind === b.kind && a.id.toLowerCase() === b.id.toLowerCase();

// ── Shared response refs ────────────────────────────────────────────────────

export interface ClusterRef {
	cluster_id: string;
	code: string;
	name: string;
}

/** A place on a trip (or an option of the place picker). */
export interface TransportPlaceRef {
	kind: PlaceKind;
	/** `venue_id`, `entry_point_id` or `transport_place_id`, by `kind`. */
	id: string;
	name: string;
	/** Venue / entry point code; null for a transport place. */
	code: string | null;
	/** Null only for an entry point or venue not given a cluster yet. */
	cluster: ClusterRef | null;
}

export interface TransportProviderRef {
	transport_provider_id: string;
	name: string;
}

export interface VehicleRef {
	vehicle_id: string;
	vehicle_type: VehicleType;
	plate: string;
	model: string;
	seats: number;
	has_ramp: boolean;
}

export interface DriverRef {
	driver_id: string;
	name: string;
	/** E.164. */
	phone: string;
}

/** A flight a transfer follows, as transport screens show it ("Follows MH 803 (+2 h 10)"). */
export interface TransportFlightRef {
	flight_id: string;
	carrier_code: string;
	flight_number: string;
	status: FlightStatus;
	scheduled_at: string | null;
	expected_at: string | null;
}

// ── Suggestions (computed, never stored) ────────────────────────────────────

/**
 * `GET /transport/suggestions` — APPROVED cards' legs with `needs_transport`,
 * a known `expected_at` and no live transfer, in the range, grouped by
 * flight (or M9's land/sea group key) × entry point × destination. ONE
 * query; at most TRANSPORT_SUGGESTIONS_LIMIT groups (`truncated`). Dispatch
 * only (`transport.dispatch`, or admin); cards via `cardReadableWith`.
 * `to` defaults to `from`; at most TRANSPORT_SUGGESTION_RANGE_DAYS_MAX days.
 */
export function makeTransportSuggestionsQuery(m: TM = enTransportFormMessages) {
	return z
		.object({
			games_id: uuid,
			direction: z.enum(TRAVEL_DIRECTIONS),
			from: dateField(m),
			to: dateField(m).optional(),
			entry_point_id: uuid.optional(),
			responsible_organization_id: uuid.optional(),
		})
		.superRefine((v, ctx) => {
			if (v.to === undefined) return;
			const days = daysBetween(v.from, v.to);
			if (days < 0) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: m.rangeInvalid });
			else if (days + 1 > TRANSPORT_SUGGESTION_RANGE_DAYS_MAX) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: m.rangeTooLong });
		});
}
export const transportSuggestionsQuery = makeTransportSuggestionsQuery();
export type TransportSuggestionsQuery = z.output<typeof transportSuggestionsQuery>;
export type TransportSuggestionsQueryInput = z.input<typeof transportSuggestionsQuery>;

/** One suggested transfer: a group of legs that would ride together. */
export interface TransportSuggestion {
	/** Opaque and stable while the group is unchanged (flight / group key × entry point × destination). */
	suggestion_key: string;
	direction: TravelDirection;
	travel_date: string;
	flight: TransportFlightRef | null;
	/** Land / sea service, or a flight typed without a flight row. */
	service_no: string | null;
	entry_point: EntryPointRef;
	/** The other end: the contingent's default drop-off (X5). Null = "Destination not set" — the desk picks one. Several contingents with different drop-offs are different suggestions. */
	place: TransportPlaceRef | null;
	/** ARRIVAL: the last expected landing; DEPARTURE: the first expected departure. */
	expected_at: string;
	/** ARRIVAL: expected_at + dwell; DEPARTURE: expected_at − lead − drive. */
	proposed_pickup_at: string;
	drive_minutes: number;
	/** The cluster of the end that is not the entry point; null when the place is not set. */
	dispatch_cluster: ClusterRef | null;
	people: number;
	wheelchair: number;
	/** At most TRANSPORT_SUGGESTION_ORGS_MAX, by size; the rest counted in `organizations_more`. */
	organizations: AccreditationOrgRef[];
	organizations_more: number;
	/** At most TRANSPORT_TRANSFER_LEGS_MAX (a larger group is cut, and `people` still counts all). */
	leg_ids: string[];
	/**
	 * The A&D journey these legs ride on — the `group_key` of `/travel/journeys`
	 * (`f:<flight_id>`, or a land / sea service slot), built by the same SQL, so
	 * `/logistics/arrival-departure/journeys/<direction>/<journey_key>` opens it.
	 */
	journey_key: string;
}

/**
 * A leg that needs transport on the day but is not suggested yet
 * (`TRANSPORT_WAITING_REASONS`) — shown greyed, read-only, so the desk sees
 * who is coming. Dispatch only, like the suggestions.
 */
export interface TransportWaitingLeg {
	travel_leg_id: string;
	ac_card_id: string;
	reason: TransportWaitingReason;
	card_status: AcCardStatus;
	/** The printed name (accreditation name, else the person's). */
	name: string;
	/** `REG-THA-000123` (`formatRegistrationNo`). */
	registration_no_display: string;
	responsible_organization: AccreditationOrgRef;
	travel_date: string;
	/** Null = time not known yet. */
	expected_at: string | null;
	flight: TransportFlightRef | null;
	service_no: string | null;
	entry_point: EntryPointRef;
	/** As `TransportSuggestion.journey_key`. */
	journey_key: string;
}

/**
 * Why a leg that needs transport is not suggested yet (the waiting list,
 * greyed and read-only — no transfer until both are settled):
 *   not_approved  its card is not APPROVED yet (checked first)
 *   time_unknown  APPROVED, but the leg has a date and no time yet
 */
export const TRANSPORT_WAITING_REASONS = ["not_approved", "time_unknown"] as const;
export type TransportWaitingReason = (typeof TRANSPORT_WAITING_REASONS)[number];

/** Per local day of the range: what is waiting, and what cannot be suggested yet. */
export interface TransportSuggestionDay {
	date: string;
	suggestions: number;
	people: number;
	/** Legs needing transport with no expected time yet ("time not known yet"). */
	time_unknown: number;
	/** Legs needing transport whose card is not APPROVED yet. */
	not_approved: number;
}

/**
 * `GET /transport/suggestions`. "Can merge" (§0b Q3) is the client's call on
 * this list: same direction, entry point and place, `expected_at` within the
 * games' `group_spread_minutes` — the desk merges them with one click
 * (`POST /transport/transfers` with one group holding both groups' legs).
 */
export interface TransportSuggestionsResult {
	items: TransportSuggestion[];
	truncated: boolean;
	days: TransportSuggestionDay[];
	/**
	 * Legs of the range that need transport but cannot be suggested yet: the
	 * first TRANSPORT_SUGGESTION_WAITING_LIMIT by expected time (no time last),
	 * `total` = Σ `days[].time_unknown + days[].not_approved`.
	 */
	waiting: { items: TransportWaitingLeg[]; total: number };
	/** `games.timezone` — transport screens label times in it (MYT). */
	timezone: string;
	group_spread_minutes: number;
}

/**
 * `GET /transport/suggestions/day-counts?games_id=&from=&to=` — per local
 * day (games' zone) and direction, the people waiting for a transfer, for the
 * Overview's day chips. `to` defaults to `from`; at most
 * TRANSPORT_SUGGESTION_DAY_COUNTS_RANGE_DAYS_MAX days. ONE statement over the
 * SAME leg rule as `/transport/suggestions` (`suggestionLegsFrom`), so a
 * day's `people` = that day's suggestions' Σ `people` (= the summary's
 * `suggestions_waiting` over both directions). Dispatch only.
 */
export function makeTransportSuggestionDayCountsQuery(m: TM = enTransportFormMessages) {
	return z
		.object({ games_id: uuid, from: dateField(m), to: dateField(m).optional() })
		.superRefine((v, ctx) => {
			if (v.to === undefined) return;
			const days = daysBetween(v.from, v.to);
			if (days < 0) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: m.rangeInvalid });
			else if (days + 1 > TRANSPORT_SUGGESTION_DAY_COUNTS_RANGE_DAYS_MAX)
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: `At most ${TRANSPORT_SUGGESTION_DAY_COUNTS_RANGE_DAYS_MAX} days` });
		});
}
export const transportSuggestionDayCountsQuery = makeTransportSuggestionDayCountsQuery();
export type TransportSuggestionDayCountsQuery = z.output<typeof transportSuggestionDayCountsQuery>;

/** One direction of one day: who is waiting for a transfer, and who cannot be suggested yet. */
export interface TransportSuggestionDayDirection {
	/** People on suggestions (APPROVED, time known, no live transfer) — the day chip's badge. */
	people: number;
	/** Card not APPROVED yet. */
	not_approved: number;
	/** APPROVED, no time yet. */
	time_unknown: number;
}

/** One day of `GET /transport/suggestions/day-counts`. */
export interface TransportSuggestionDayCount {
	/** YYYY-MM-DD, the games' zone. */
	date: string;
	arrivals: TransportSuggestionDayDirection;
	departures: TransportSuggestionDayDirection;
}

/** `GET /transport/suggestions/day-counts` — only the days with anyone waiting, ascending. */
export interface TransportSuggestionDayCounts {
	days: TransportSuggestionDayCount[];
	timezone: string;
}

/**
 * `POST /transport/transfers` — "Create N transfers" / "Merge into one":
 * one request per group, its passengers the legs' cards. A leg already on a
 * live transfer, not eligible, or out of scope is skipped (`skipped[]`); a
 * group left with nobody is not created; a single group left with nobody is
 * 409 TRANSFER_EXISTS (`details.refs`). `place` overrides the drop-off,
 * `pickup_at` fixes the time (`follows_travel = false`), and
 * `dispatch_cluster_id` overrides the derived cluster.
 */
export function makeCreateTransfersRequest(m: TM = enTransportFormMessages) {
	return z.object({
		games_id: uuid,
		direction: z.enum(TRAVEL_DIRECTIONS),
		groups: z
			.array(
				z.object({
					leg_ids: z.array(uuid).min(1, m.required).max(TRANSPORT_TRANSFER_LEGS_MAX).refine(uniqueIds, m.idsDuplicate),
					place: placeInput.optional(),
					pickup_at: dateTime(m).optional(),
					dispatch_cluster_id: uuid.optional(),
				}),
			)
			.min(1, m.required)
			.max(TRANSPORT_TRANSFER_GROUPS_MAX)
			.refine((groups) => uniqueIds(groups.flatMap((g) => g.leg_ids)), m.idsDuplicate),
	});
}
export const createTransfersBody = makeCreateTransfersRequest();
export type CreateTransfersBody = z.input<typeof createTransfersBody>;

export interface TransportCreatedRef {
	transport_request_id: string;
	ref_no: number;
	/** `formatTripRef(ref_no)`. */
	ref: string;
	passengers: number;
}

/**
 * `POST /transport/requests`. An ARR / DEP transfer links each named
 * person's leg of that direction (so they leave the suggestions, and the
 * transfer is theirs in A&D and on the record) — unless that leg already
 * rides on another live transfer: the person still rides, without the leg,
 * and is listed in `already_on_transfer`. An ADHOC trip links nothing (0, []).
 */
export interface TransportCreatedTrip extends TransportCreatedRef {
	legs_linked: number;
	already_on_transfer: Array<{ ac_card_id: string; ref: string }>;
}

export interface CreateTransfersResult {
	created: TransportCreatedRef[];
	skipped: Array<{ travel_leg_id: string; reason: TransportTransferSkipReason; ref: string | null }>;
}

/** `POST /transport/suggestions/dismiss` — "no transport needed": the legs' `needs_transport` becomes false. ONE UPDATE, legs in scope only. */
export function makeDismissSuggestionsRequest(m: TM = enTransportFormMessages) {
	return z.object({
		games_id: uuid,
		leg_ids: z.array(uuid).min(1, m.required).max(TRANSPORT_TRANSFER_LEGS_MAX).refine(uniqueIds, m.idsDuplicate),
	});
}
export const dismissSuggestionsBody = makeDismissSuggestionsRequest();
export type DismissSuggestionsBody = z.input<typeof dismissSuggestionsBody>;

export interface DismissSuggestionsResult {
	dismissed: number;
}

// ── Requests: create, edit, passengers, status ──────────────────────────────

/**
 * `POST /transport/requests` — a venue trip (ADHOC), or a host ARR / DEP
 * transfer without legs. ORGANIZATION: ADHOC only, its own organisation, and
 * `pickup_at` ≥ now + `noc_lead_hours` (hard; refusal 422 TRIP_LEAD_TIME with
 * `details.min_pickup_at`); the host is not bound. Named passengers are
 * APPROVED cards the caller may read with `transport.request` / dispatch, at
 * most TRANSPORT_PASSENGERS_MAX; `unnamed_count` adds people typed as a count
 * (a NOC's head count, escorts). `responsible_organization_id` null = the
 * host's own trip. `drive_minutes` omitted → the cluster matrix (or the
 * games default); `dispatch_cluster_id` omitted → derived (X3/X4).
 */
export function makeCreateTransportRequest(m: TM = enTransportFormMessages) {
	return z
		.object({
			games_id: uuid,
			source: z.enum(TRANSPORT_REQUEST_SOURCES).default("ADHOC"),
			responsible_organization_id: uuid.nullable(),
			from: placeInput,
			to: placeInput,
			pickup_at: dateTime(m),
			return_pickup_at: dateTime(m).nullable().optional().transform((v) => v ?? null),
			drive_minutes: driveMinutes(m).optional(),
			dispatch_cluster_id: uuid.optional(),
			ac_card_ids: z.array(uuid).max(TRANSPORT_PASSENGERS_MAX, m.passengersMax).refine(uniqueIds, m.idsDuplicate).default([]),
			unnamed_count: count(m, TRANSPORT_UNNAMED_MAX).default(0),
			wheelchair_unnamed: count(m, TRANSPORT_UNNAMED_MAX).default(0),
			bags: count(m, TRANSPORT_BAGS_MAX).default(0),
			contact_name: nullableText(m, TRANSPORT_NAME_MAX),
			contact_phone: nullableText(m, TRANSPORT_PHONE_MAX),
			remarks: nullableText(m, TRANSPORT_REMARKS_MAX),
		})
		.superRefine((v, ctx) => {
			const issue = (path: string, message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
			if (samePlace(v.from, v.to)) issue("to", m.samePlace);
			if (v.source === "ARR" && v.from.kind !== "ENTRY_POINT") issue("from", m.entryPointEnd);
			if (v.source === "DEP" && v.to.kind !== "ENTRY_POINT") issue("to", m.entryPointEnd);
			if (v.return_pickup_at !== null && (v.source !== "ADHOC" || Date.parse(v.return_pickup_at) <= Date.parse(v.pickup_at))) issue("return_pickup_at", m.returnInvalid);
			if (v.ac_card_ids.length + v.unnamed_count < 1) issue("unnamed_count", m.passengersRequired);
			if (v.wheelchair_unnamed > v.unnamed_count) issue("wheelchair_unnamed", m.wheelchairTooMany);
		});
}
export const createTransportRequestBody = makeCreateTransportRequest();
export type CreateTransportRequestBody = z.input<typeof createTransportRequestBody>;

/**
 * `PATCH /transport/requests/:id` — any subset, plus `version` (→ 409
 * STALE_VERSION). A guarded UPDATE (`tripEditAllowed`): OPEN and not
 * started; a requester edits its ADHOC trips only, within the lead time.
 * Setting `pickup_at` fixes the time (`follows_travel` becomes false);
 * `follows_travel: true` ties an ARR / DEP pickup back to its legs. A change
 * of time, drive minutes or place re-times the allocations in the same
 * transaction (a clash releases them: DELAY_CLASH, `needs_redispatch`).
 * Host-only fields (`dispatch_cluster_id`, `drive_minutes`, `follows_travel`)
 * from a requester → 403 FORBIDDEN_SCOPE.
 */
export function makePatchTransportRequest(m: TM = enTransportFormMessages) {
	return z
		.object({
			version: z.number().int().min(1),
			from: placeInput.optional(),
			to: placeInput.optional(),
			pickup_at: dateTime(m).optional(),
			return_pickup_at: dateTime(m).nullable().optional(),
			follows_travel: z.literal(true).optional(),
			drive_minutes: driveMinutes(m).optional(),
			dispatch_cluster_id: uuid.optional(),
			unnamed_count: count(m, TRANSPORT_UNNAMED_MAX).optional(),
			wheelchair_unnamed: count(m, TRANSPORT_UNNAMED_MAX).optional(),
			bags: count(m, TRANSPORT_BAGS_MAX).optional(),
			contact_name: patchText(m, TRANSPORT_NAME_MAX),
			contact_phone: patchText(m, TRANSPORT_PHONE_MAX),
			remarks: patchText(m, TRANSPORT_REMARKS_MAX),
			/** Headline for this change in the Activity feed. */
			reason: z.string().trim().min(1).max(TRANSPORT_REASON_MAX, m.textTooLong).optional(),
		})
		.superRefine((v, ctx) => {
			const { version: _v, reason: _r, ...rest } = v;
			if (!Object.values(rest).some((x) => x !== undefined)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: m.nothingToChange });
			if (v.from && v.to && samePlace(v.from, v.to)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: m.samePlace });
			if (v.pickup_at !== undefined && v.follows_travel) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["follows_travel"], message: m.timeConflict });
			if (v.pickup_at && v.return_pickup_at && Date.parse(v.return_pickup_at) <= Date.parse(v.pickup_at))
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["return_pickup_at"], message: m.returnInvalid });
			if (v.unnamed_count !== undefined && v.wheelchair_unnamed !== undefined && v.wheelchair_unnamed > v.unnamed_count)
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["wheelchair_unnamed"], message: m.wheelchairTooMany });
		});
}
export const patchTransportRequestBody = makePatchTransportRequest();
export type PatchTransportRequestBody = z.input<typeof patchTransportRequestBody>;

/**
 * `PUT /transport/requests/:id/passengers` — replace-set of the named
 * passengers (APPROVED cards in scope) plus the unnamed count. Cards dropped
 * are released (`released_at`), never deleted. Same rule as PATCH.
 */
export function makeSetTransportPassengersRequest(m: TM = enTransportFormMessages) {
	return z
		.object({
			version: z.number().int().min(1),
			ac_card_ids: z.array(uuid).max(TRANSPORT_PASSENGERS_MAX, m.passengersMax).refine(uniqueIds, m.idsDuplicate),
			unnamed_count: count(m, TRANSPORT_UNNAMED_MAX),
			wheelchair_unnamed: count(m, TRANSPORT_UNNAMED_MAX).default(0),
		})
		.superRefine((v, ctx) => {
			if (v.ac_card_ids.length + v.unnamed_count < 1) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["unnamed_count"], message: m.passengersRequired });
			if (v.wheelchair_unnamed > v.unnamed_count) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["wheelchair_unnamed"], message: m.wheelchairTooMany });
		});
}
export const setTransportPassengersBody = makeSetTransportPassengersRequest();
export type SetTransportPassengersBody = z.input<typeof setTransportPassengersBody>;

/**
 * `POST /transport/requests/bulk/status` — Cancel or Reject, with a required
 * reason (the requester sees it). ONE UPDATE with the edge and the rule in
 * its WHERE; the others come back in `skipped`. Cancelling releases the
 * passengers and the ASSIGNED allocations (trigger).
 */
export function makeTransportBulkStatusRequest(m: TM = enTransportFormMessages) {
	return z.object({
		ids: z.array(uuid).min(1, m.required).max(TRANSPORT_BULK_MAX).refine(uniqueIds, m.idsDuplicate),
		action: z.enum(TRANSPORT_BULK_ACTIONS),
		reason: reasonText(m),
	});
}
export const transportBulkStatusBody = makeTransportBulkStatusRequest();
export type TransportBulkStatusBody = z.input<typeof transportBulkStatusBody>;

export interface TransportBulkStatusResult {
	updated: Array<{ transport_request_id: string; ref: string }>;
	skipped: Array<{ transport_request_id: string; ref: string | null; reason: TransportBulkSkipReason }>;
}

/** `POST /transport/allocations/:id/cancel` — a vehicle drops out (breakdown, no-show): `cancel_reason = VEHICLE_UNAVAILABLE`; the request gets `needs_redispatch`. */
export function makeCancelAllocationRequest(m: TM = enTransportFormMessages) {
	return z.object({ reason: reasonText(m) });
}
export const cancelAllocationBody = makeCancelAllocationRequest();
export type CancelAllocationBody = z.input<typeof cancelAllocationBody>;

// ── Requests: list, detail ──────────────────────────────────────────────────

/**
 * `GET /transport/requests` — keyset on `(pickup_at, transport_request_id)`
 * (see TRANSPORT_REQUEST_TABS):
 *   upcoming  ascending from now, and ALSO the trips still on the road (a
 *             vehicle EN_ROUTE, or PARTIAL) even when their pickup has passed;
 *   past      descending, pickup passed and nothing on the road;
 *   day       every trip of `date` (required), ascending, any state.
 * `cursor` is opaque — send back `next_cursor` as received; `total` comes
 * with the first page only — and NOT when `status` or `attention` is set
 * without `date` (`total: null`; keyset paging still works): such a total has
 * to derive the status of every trip in the walk (~14k upcoming at the 60k
 * fixture, 55–235 ms), so the client shows "Load more" without a count. `date` = one local day (games' time zone); on
 * upcoming / past it narrows to that day. `status` = display statuses,
 * comma-separated. Scope: admins all; a scoped group with `transport.request`
 * / dispatch its organisations; a provider the trips on its vehicles. The
 * row shape depends on the caller (`view`, `TransportRequestsPage`).
 */
export function makeTransportRequestsQuery(m: TM = enTransportFormMessages) {
	return z.object({
		games_id: uuid,
		tab: z.enum(TRANSPORT_REQUEST_TABS).default("upcoming"),
		date: dateField(m).optional(),
		status: enumList(TRANSPORT_DISPLAY_STATUSES).optional(),
		source: z.enum(TRANSPORT_REQUEST_SOURCES).optional(),
		cluster_id: uuid.optional(),
		responsible_organization_id: uuid.optional(),
		wheelchair: booleanParam.optional(),
		redispatch: booleanParam.optional(),
		/** Trip reference (`parseTripRefQuery`), organisation code / name, place, requester. */
		q: searchText.optional(),
		cursor: z.string().min(1).max(200).optional(),
		limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(TRANSPORT_REQUESTS_PAGE_SIZE_DEFAULT),
		/**
		 * (S3, appended) The trips counted under this Needs-attention kind of
		 * `GET /transport/summary` — the SAME SQL predicate, time window
		 * included (TRANSPORT_ATTENTION_WINDOWS). When set, the kind's window
		 * replaces the tab's (`tab` / `date` are not applied), ascending by
		 * pickup; the other filters still narrow it.
		 */
		attention: z.enum(TRANSPORT_ATTENTION_KINDS).optional(),
	}).refine((v) => v.tab !== "day" || v.date !== undefined || v.attention !== undefined, { path: ["date"], message: m.dateInvalid });
}
export const transportRequestsQuery = makeTransportRequestsQuery();
export type TransportRequestsQuery = z.output<typeof transportRequestsQuery>;
export type TransportRequestsQueryInput = z.input<typeof transportRequestsQuery>;

/** One vehicle on a trip, as the dispatch desk sees it. */
export interface TransportAllocationLine {
	transport_allocation_id: string;
	state: TransportAllocationState;
	cancel_reason: TransportAllocationCancelReason | null;
	vehicle: VehicleRef;
	driver: DriverRef;
	provider: TransportProviderRef | null;
	pax: number;
	started_at: string | null;
	arrived_at: string | null;
}

/** The common head of every view of a request. */
interface TransportTripHead {
	transport_request_id: string;
	ref_no: number;
	ref: string;
	games_id: string;
	source: TransportRequestSource;
	state: TransportRequestState;
	/** Why it was cancelled / rejected (the requester sees it). */
	state_reason: string | null;
	from: TransportPlaceRef;
	to: TransportPlaceRef;
	pickup_at: string;
	return_pickup_at: string | null;
	drive_minutes: number;
	/** The pickup follows the passengers' legs (ARR / DEP); false = a fixed time. */
	follows_travel: boolean;
	/** Someone else changed it since this login last opened it (`PUT /seen/transport_request/:id`; never opened = false). */
	changed: boolean;
}

/** FULL view (admins, dispatch desks): one row of the list. */
export interface TransportRequestRow extends TransportTripHead {
	view: "full";
	status: TransportDisplayStatus;
	/** NULL = the host's own trip. Merged transfers name the request's organisation only; passengers carry theirs. */
	responsible_organization: AccreditationOrgRef | null;
	/** Organisations of the passengers (merged transfers have several), by size. */
	organization_codes: string[];
	dispatch_cluster: ClusterRef;
	/** Named passengers (not released) + `unnamed_count`. */
	passengers: number;
	wheelchair: number;
	bags: number;
	/** Σ pax of the allocations that are not CANCELLED. */
	seats_assigned: number;
	needs_redispatch: boolean;
	/** An ASSIGNED allocation's driver is now off shift / on a rest day (Q4: kept, flagged). */
	recheck: boolean;
	/** The distinct flights of the passengers' legs (a merged transfer has several). */
	flights: TransportFlightRef[];
	/** (E2E B3) The services without a flight row its passengers' legs follow: a coach, a ferry, a typed flight ("Follows Coach SG-1"). */
	services: TransportServiceRef[];
	allocations: TransportAllocationLine[];
	requested_by_name: string | null;
	created_at: string;
}

/**
 * NOC view (`transport.request` without dispatch): §0b Q3 is a HARD rule —
 * a NOC must never learn that another NOC's people share its flight or
 * vehicle. Everything here is built in SQL from the caller's OWN passengers
 * (cards in its scope) only: no total, no seats, no other organisation, no
 * other passenger, no merged group's other flights, no pax per vehicle.
 * `status` never says SHORT (it would reveal more people than the caller's
 * own): a SHORT trip shows as ASSIGNED.
 */
export interface TransportNocTripRow extends TransportTripHead {
	view: "noc";
	status: Exclude<TransportDisplayStatus, "SHORT">;
	/** The caller's own named passengers on it, plus — on the caller's own trip — its unnamed count. */
	own_passengers: number;
	own_wheelchair: number;
	/** Flights of the caller's own passengers only. */
	flights: TransportFlightRef[];
	/** (E2E B3) Services without a flight row its OWN passengers' legs follow. */
	services: TransportServiceRef[];
	/** Vehicles and drivers, without pax or seats. */
	vehicles: Array<{ state: TransportAllocationState; vehicle_type: VehicleType; plate: string; driver_name: string; driver_phone: string }>;
	/** Its own ADHOC trip, OPEN, not started. */
	can_cancel: boolean;
}

/** One organisation's group on a trip, as a provider sees it: a count and whom to call, never names. */
export interface TransportProviderGroup {
	/** Null = the host's own people. */
	organization_code: string | null;
	organization_name: string | null;
	passengers: number;
	wheelchair: number;
	/** The group's lead: the request's contact for the request's own organisation; null when none is known. */
	lead_contact: { name: string | null; phone: string | null } | null;
}

/**
 * Provider view (TRANSPORT_PROVIDER, §0b Q1): the trips on its vehicles,
 * passenger COUNTS per organisation group and each group lead's contact — no
 * name list, no card numbers. Only its own allocations are listed. A
 * separate type on purpose: never the full row with fields nulled.
 */
export interface TransportProviderTripRow extends TransportTripHead {
	view: "provider";
	status: TransportDisplayStatus;
	/** The trip's total (its vehicles carry part or all of it). */
	passengers: number;
	wheelchair: number;
	bags: number;
	groups: TransportProviderGroup[];
	remarks: string | null;
	allocations: Array<TransportAllocationLine & { can_start: boolean; can_arrive: boolean }>;
}

/** Shorter names for the two restricted rows (the same types). */
export type TransportNocTrip = TransportNocTripRow;
export type TransportProviderTrip = TransportProviderTripRow;

export type TransportTrip = TransportRequestRow | TransportNocTripRow | TransportProviderTripRow;

/**
 * `GET /transport/requests` — keyset; one view per caller (`view`). `total` on
 * the first page only, else null — and null on the first page too when
 * `status` or `attention` is set without `date` (see makeTransportRequestsQuery).
 */
export interface TransportRequestsPage {
	items: TransportTrip[];
	next_cursor: string | null;
	total: number | null;
}

/** A named passenger, as the full view lists them. */
export interface TransportPassenger extends PersonRoleRefs {
	ac_card_id: string;
	registration_no_display: string;
	accreditation_no_display: string | null;
	card_status: AcCardStatus;
	person: { people_id: string; printed_name: string; country_code: string; wheelchair_user: boolean };
	responsible_organization: AccreditationOrgRef;
	/** The leg it follows (transfers), else null. */
	leg: { travel_leg_id: string; direction: TravelDirection; expected_at: string | null; flight: TransportFlightRef | null; service_no: string | null } | null;
}

/** One issue the allocation check found. */
export interface TransportCheckIssue<C extends string = TransportCheckCode> {
	code: C;
	/** VEHICLE_BUSY / DRIVER_BUSY: the other trip's ref. */
	ref: string | null;
	/** SEATS_SHORT / SEATS_OVER / RAMP_REQUIRED: how many. */
	n: number | null;
}

/**
 * `transport_check_allocations` as the wire carries it: on the detail
 * (`checks`, over the live allocations) and as 422 ALLOCATION_INVALID's
 * `details`. `rows[].index` is the row's position in the PUT body.
 */
export interface TransportCheckResult {
	rows: Array<{ index: number; issues: TransportCheckIssue<TransportRowCheckCode>[] }>;
	totals: TransportCheckIssue<TransportTotalCheckCode>[];
	seats_needed: number;
	seats_assigned: number;
	/** No blocking issue (TRANSPORT_CHECK_WARNINGS aside; SEATS_SHORT aside with `accept_partial`). */
	ok: boolean;
}

/** What the caller may do — the same rule builders the writes use (`transport-rules.ts`). The UI offers these and nothing else. */
export interface TransportRequestActions {
	edit: boolean;
	passengers: boolean;
	allocate: boolean;
	cancel: boolean;
	reject: boolean;
}

/** `GET /transport/requests/:id`, full view. */
export interface TransportRequestDetail extends TransportRequestRow {
	passenger_list: TransportPassenger[];
	unnamed_count: number;
	wheelchair_unnamed: number;
	/** Allocations including CANCELLED ones (history of the trip), each with what the caller may press. */
	allocations: Array<TransportAllocationLine & { can_start: boolean; can_arrive: boolean; can_cancel: boolean }>;
	checks: TransportCheckResult;
	actions: TransportRequestActions;
	contact_name: string | null;
	contact_phone: string | null;
	remarks: string | null;
	confirmed_at: string | null;
	confirmed_by_name: string | null;
	version: number;
	updated_at: string;
}

/** `GET /transport/requests/:id` for a NOC: its own passengers by name, nothing of anyone else's. */
export interface TransportNocTripDetail extends TransportNocTripRow {
	own_passenger_list: TransportPassenger[];
	contact_name: string | null;
	contact_phone: string | null;
	remarks: string | null;
	actions: TransportRequestActions;
	version: number;
}

export type TransportTripDetail = TransportRequestDetail | TransportNocTripDetail | TransportProviderTripRow;

// ── Dispatch: options, suggest, allocations, auto-assign ────────────────────

/** `GET /transport/requests/:id/options` — vehicles of the dispatch cluster first, then the rest. */
export interface TransportVehicleOption extends VehicleRef {
	status: VehicleStatus;
	cluster: ClusterRef;
	provider: TransportProviderRef | null;
	in_dispatch_cluster: boolean;
	/** The trip that holds it in this request's busy window, or null when free. */
	busy_ref: string | null;
}

export interface TransportDriverOption extends DriverRef {
	cluster: ClusterRef;
	provider: TransportProviderRef | null;
	vehicle_types: VehicleType[];
	shift_start: string;
	shift_end: string;
	rest_isodow: number | null;
	usual_vehicle_ids: string[];
	in_dispatch_cluster: boolean;
	/** The window fits inside a shift (`transport_driver_fits`). */
	on_shift: boolean;
	rest_day: boolean;
	busy_ref: string | null;
}

export interface TransportAllocationOptions {
	vehicles: TransportVehicleOption[];
	drivers: TransportDriverOption[];
}

/** `GET /transport/requests/:id/suggest` — her greedy pick, written nowhere. */
export interface TransportSuggestResult {
	rows: Array<{ vehicle_id: string; driver_id: string; pax: number }>;
	/** People still without a seat. */
	left: number;
	why: TransportSuggestWhy | null;
	ok: boolean;
}

/**
 * `PUT /transport/requests/:id/allocations` — replace the trip's vehicles.
 * Rows keep their id when they existed (`transport_allocation_id`); dropped
 * rows become CANCELLED (EDITED). Ids are nullable and pax may be 0 on
 * purpose: the server's check answers with per-row codes (VEHICLE_REQUIRED,
 * PAX_INVALID, …) in 422 ALLOCATION_INVALID, nothing written. Something on
 * the road → 409 TRIP_LOCKED; a race on a vehicle or driver → 409
 * RESOURCE_BUSY; `version` → STALE_VERSION.
 */
export function makeSetAllocationsRequest(m: TM = enTransportFormMessages) {
	return z.object({
		version: z.number().int().min(1),
		rows: z
			.array(
				z.object({
					transport_allocation_id: uuid.optional(),
					vehicle_id: uuid.nullable(),
					driver_id: uuid.nullable(),
					pax: count(m, TRANSPORT_PAX_MAX),
				}),
			)
			.max(TRANSPORT_ALLOCATION_ROWS_MAX, m.rowsMax),
		accept_partial: z.boolean().default(false),
	});
}
export const setAllocationsBody = makeSetAllocationsRequest();
export type SetAllocationsBody = z.input<typeof setAllocationsBody>;

/**
 * Vehicles and drivers are SUGGESTED, then confirmed (user, 2026-11-03: auto
 * assign is "sensitive and dangerous"). `POST /transport/auto-assign/suggest`
 * runs her greedy algorithm over the requests in pickup order, one at a time
 * (each sees the previous ones' allocations), reads back what it picked and
 * ROLLS BACK. `POST /transport/auto-assign` runs it again for the same ids
 * and keeps it only when the picks equal the reviewed `fingerprint`; else
 * nothing is saved and `changed` is true. Ids the caller may not dispatch
 * come back SKIPPED `NOT_DISPATCHABLE`.
 */
export function makeAutoAssignSuggestRequest(m: TM = enTransportFormMessages) {
	return z.object({
		request_ids: z.array(uuid).min(1, m.required).max(TRANSPORT_AUTO_ASSIGN_MAX).refine(uniqueIds, m.idsDuplicate),
		accept_partial: z.boolean().default(false),
	});
}
export const autoAssignSuggestBody = makeAutoAssignSuggestRequest();
export type AutoAssignSuggestBody = z.input<typeof autoAssignSuggestBody>;

export function makeAutoAssignRequest(m: TM = enTransportFormMessages) {
	return makeAutoAssignSuggestRequest(m).extend({ fingerprint: z.string().trim().min(1).max(128) });
}
export const autoAssignBody = makeAutoAssignRequest();
export type AutoAssignBody = z.input<typeof autoAssignBody>;

/** One vehicle + driver a suggestion would put on a trip. */
export interface AutoAssignPick {
	transport_request_id: string;
	vehicle_plate: string;
	vehicle_type: string;
	driver_name: string;
	pax: number;
}

export interface AutoAssignResult {
	results: Array<{ transport_request_id: string; ref: string | null; outcome: TransportAutoAssignOutcome; reason: TransportAutoAssignReason | null; vehicles: number }>;
	assigned: number;
	partial: number;
	skipped: number;
	/** Apply only: the picks moved since the review, so NOTHING was saved — suggest again. Always false on a suggestion. */
	changed: boolean;
}

/** `POST /transport/auto-assign/suggest`: nothing was saved. */
export interface AutoAssignSuggestion extends AutoAssignResult {
	picks: AutoAssignPick[];
	/** Send back unchanged to `POST /transport/auto-assign`. */
	fingerprint: string;
}

// ── Summary ─────────────────────────────────────────────────────────────────

/** `GET /transport/summary?games_id=&date=` — the Overview, ONE statement of scalar subqueries, in the caller's scope. */
export function makeTransportSummaryQuery(m: TM = enTransportFormMessages) {
	return z.object({ games_id: uuid, date: dateField(m).optional() });
}
export const transportSummaryQuery = makeTransportSummaryQuery();
export type TransportSummaryQuery = z.output<typeof transportSummaryQuery>;

/**
 * Enum members are VALUES, never keys (the wire's keys are snake-cased at the
 * boundary): each count list carries EVERY member of its enum, in the
 * contract's order, zeros kept — index it by position or `find` by value.
 */
export interface TransportStatusCount<S extends TransportDisplayStatus = TransportDisplayStatus> {
	status: S;
	count: number;
}
export interface TransportAttentionCount {
	kind: TransportAttentionKind;
	count: number;
}

export interface TransportSummary {
	date: string;
	timezone: string;
	/** Trips picking up on `date`, by derived status: every TRANSPORT_DISPLAY_STATUSES member, in order. */
	by_status: TransportStatusCount[];
	/** PENDING with the pickup passed (= the OVERDUE attention count). */
	overdue: number;
	/** PENDING / SHORT with the pickup in the next 24 h. */
	next_24h_unassigned: number;
	/** Every TRANSPORT_ATTENTION_KINDS member, in order (windows: TRANSPORT_ATTENTION_WINDOWS). */
	attention: TransportAttentionCount[];
	on_the_road: { trips: number; drivers: number };
	/** Active drivers by their status now: every DRIVER_NOW_STATUSES member, in order. */
	drivers: { by_status: Array<{ status: DriverNowStatus; count: number }>; total: number };
	/** Not RETIRED; `by_type` every VEHICLE_TYPES member, in order. */
	vehicles: { total: number; maintenance: number; by_type: Array<{ vehicle_type: VehicleType; count: number }> };
	/** Suggestions waiting on `date` (people, not groups). */
	suggestions_waiting: number;
	/**
	 * Of `suggestions_waiting`, the people with no destination: no hotel (a room
	 * in a building linked to an active transport place) and no contingent
	 * drop-off — their transfer needs a place picked (Needs attention).
	 */
	suggestions_no_destination: number;
}

// ── Fleet: vehicles ─────────────────────────────────────────────────────────

export const VEHICLE_SORT_KEYS = ["plate", "vehicle_type", "seats", "created_at"] as const;
export type VehicleSortKey = (typeof VEHICLE_SORT_KEYS)[number];

/** `GET /transport/vehicles` — offset (a few hundred rows). A provider login sees its own. */
export const listVehiclesQuery = z.object({
	...pageParams,
	/** Plate, model or remarks. */
	search: searchText.optional(),
	vehicle_type: z.enum(VEHICLE_TYPES).optional(),
	status: z.enum(VEHICLE_STATUSES).optional(),
	cluster_id: uuid.optional(),
	provider_id: uuid.optional(),
	has_ramp: booleanParam.optional(),
	sort: z.enum(VEHICLE_SORT_KEYS).optional(),
	dir: dirParam.optional(),
});
export type ListVehiclesQuery = z.output<typeof listVehiclesQuery>;
export type ListVehiclesQueryInput = z.input<typeof listVehiclesQuery>;

const plateField = (m: FM) => z.string().trim().toUpperCase().min(1, m.plateInvalid).max(VEHICLE_PLATE_MAX, m.plateInvalid).regex(VEHICLE_PLATE_PATTERN, m.plateInvalid);
const seatsField = (m: FM) => z.number({ invalid_type_error: m.seatsInvalid }).int(m.seatsInvalid).min(VEHICLE_SEATS.CAR.min, m.seatsInvalid).max(VEHICLE_SEATS.BUS.max, m.seatsInvalid);

/** `POST /transport/vehicles`. Plate unique after `normalizePlate` (409 CONFLICT `{ field: "plate" }`). RETIRED is reached by PATCH only. */
export function makeCreateVehicleRequest(m: FM = enTransportFleetFormMessages) {
	return z
		.object({
			vehicle_type: z.enum(VEHICLE_TYPES, { errorMap: () => ({ message: m.required }) }),
			plate: plateField(m),
			model: z.string().trim().min(1, m.required).max(VEHICLE_MODEL_MAX, m.textTooLong),
			seats: seatsField(m),
			has_ramp: z.boolean().default(false),
			status: z.enum(["ACTIVE", "MAINTENANCE"]).default("ACTIVE"),
			cluster_id: uuid,
			provider_id: uuid.nullable().optional().transform((v) => v ?? null),
			remarks: nullableText(m, TRANSPORT_REMARKS_MAX),
		})
		.refine((v) => vehicleSeatsFit(v.vehicle_type, v.seats), { path: ["seats"], message: m.seatsInvalid });
}
export const createVehicleBody = makeCreateVehicleRequest();
export type CreateVehicleBody = z.input<typeof createVehicleBody>;

/**
 * `PATCH /transport/vehicles/:id` — any subset + `version`. Seats against the
 * STORED type is the database CHECK's job. MAINTENANCE / RETIRED, a new
 * cluster or type while upcoming allocations hold it → 409 CONFLICT with
 * `details.refs` (the trips).
 */
export function makePatchVehicleRequest(m: FM = enTransportFleetFormMessages) {
	return z
		.object({
			version: z.number().int().min(1),
			vehicle_type: z.enum(VEHICLE_TYPES).optional(),
			plate: plateField(m).optional(),
			model: z.string().trim().min(1, m.required).max(VEHICLE_MODEL_MAX, m.textTooLong).optional(),
			seats: seatsField(m).optional(),
			has_ramp: z.boolean().optional(),
			status: z.enum(VEHICLE_STATUSES).optional(),
			cluster_id: uuid.optional(),
			provider_id: uuid.nullable().optional(),
			remarks: patchText(m, TRANSPORT_REMARKS_MAX),
			reason: z.string().trim().min(1).max(TRANSPORT_REASON_MAX, m.textTooLong).optional(),
		})
		.superRefine((v, ctx) => {
			const { version: _v, reason: _r, ...rest } = v;
			if (!Object.values(rest).some((x) => x !== undefined)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: m.nothingToChange });
			if (v.vehicle_type !== undefined && v.seats !== undefined && !vehicleSeatsFit(v.vehicle_type, v.seats))
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["seats"], message: m.seatsInvalid });
		});
}
export const patchVehicleBody = makePatchVehicleRequest();
export type PatchVehicleBody = z.input<typeof patchVehicleBody>;

export interface Vehicle extends VehicleRef {
	status: VehicleStatus;
	cluster: ClusterRef;
	provider: TransportProviderRef | null;
	remarks: string | null;
	/** Allocations (not CANCELLED) picking up today, games time zone. */
	trips_today: number;
	/** Live allocations still ahead: a status / cluster / type change is refused while > 0. */
	upcoming_trips: number;
	version: number;
	created_at: string;
	updated_at: string;
}

// ── Fleet: drivers ──────────────────────────────────────────────────────────

export const DRIVER_SORT_KEYS = ["name", "cluster", "created_at"] as const;
export type DriverSortKey = (typeof DRIVER_SORT_KEYS)[number];

/** `GET /transport/drivers` — offset. `status_now` filters on DRIVER_NOW_STATUSES. A provider login sees its own. */
export const listDriversQuery = z.object({
	...pageParams,
	/** Name or phone. */
	search: searchText.optional(),
	cluster_id: uuid.optional(),
	provider_id: uuid.optional(),
	vehicle_type: z.enum(VEHICLE_TYPES).optional(),
	status_now: z.enum(DRIVER_NOW_STATUSES).optional(),
	is_active: booleanParam.optional(),
	sort: z.enum(DRIVER_SORT_KEYS).optional(),
	dir: dirParam.optional(),
});
export type ListDriversQuery = z.output<typeof listDriversQuery>;
export type ListDriversQueryInput = z.input<typeof listDriversQuery>;

const timeOfDay = (m: FM) => z.string().trim().regex(TIME_OF_DAY_PATTERN, m.timeInvalid);
const isodow = (m: FM) => z.number({ invalid_type_error: m.restDayInvalid }).int(m.restDayInvalid).min(ISODOW_MIN, m.restDayInvalid).max(ISODOW_MAX, m.restDayInvalid);
const vehicleTypes = (m: FM) =>
	z
		.array(z.enum(VEHICLE_TYPES))
		.min(1, m.vehicleTypesRequired)
		.max(VEHICLE_TYPES.length)
		.refine((t) => new Set(t).size === t.length, m.idsDuplicate);
const vehicleIds = (m: FM) => z.array(uuid).max(DRIVER_VEHICLES_MAX, m.rowsMax).refine(uniqueIds, m.idsDuplicate);
const phoneE164 = (m: FM) => z.string().trim().regex(E164_PHONE_PATTERN, m.phoneInvalid);

/**
 * `POST /transport/drivers`. `vehicle_types` is the certification. The usual
 * vehicles (`vehicle_ids`) must be of the driver's cluster and a certified
 * type (422 on `vehicle_ids`, checked in the write transaction). A shift with
 * end ≤ start runs overnight; `rest_isodow` null = no rest day.
 */
export function makeCreateDriverRequest(m: FM = enTransportFleetFormMessages) {
	return z
		.object({
			name: z.string().trim().min(1, m.nameRequired).max(TRANSPORT_NAME_MAX, m.textTooLong),
			phone: phoneE164(m),
			cluster_id: uuid,
			provider_id: uuid.nullable().optional().transform((v) => v ?? null),
			vehicle_types: vehicleTypes(m),
			shift_start: timeOfDay(m),
			shift_end: timeOfDay(m),
			rest_isodow: isodow(m).nullable().optional().transform((v) => v ?? null),
			vehicle_ids: vehicleIds(m).default([]),
			is_active: z.boolean().default(true),
		})
		.refine((v) => v.shift_start !== v.shift_end, { path: ["shift_end"], message: m.shiftEmpty });
}
export const createDriverBody = makeCreateDriverRequest();
export type CreateDriverBody = z.input<typeof createDriverBody>;

/** `PATCH /transport/drivers/:id` — any subset + `version`; `vehicle_ids` is a replace-set. A new cluster or a removed type while upcoming allocations hold the driver → 409 CONFLICT `details.refs`. */
export function makePatchDriverRequest(m: FM = enTransportFleetFormMessages) {
	return z
		.object({
			version: z.number().int().min(1),
			name: z.string().trim().min(1, m.nameRequired).max(TRANSPORT_NAME_MAX, m.textTooLong).optional(),
			phone: phoneE164(m).optional(),
			cluster_id: uuid.optional(),
			provider_id: uuid.nullable().optional(),
			vehicle_types: vehicleTypes(m).optional(),
			shift_start: timeOfDay(m).optional(),
			shift_end: timeOfDay(m).optional(),
			rest_isodow: isodow(m).nullable().optional(),
			vehicle_ids: vehicleIds(m).optional(),
			is_active: z.boolean().optional(),
			reason: z.string().trim().min(1).max(TRANSPORT_REASON_MAX, m.textTooLong).optional(),
		})
		.superRefine((v, ctx) => {
			const { version: _v, reason: _r, ...rest } = v;
			if (!Object.values(rest).some((x) => x !== undefined)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: m.nothingToChange });
			if (v.shift_start !== undefined && v.shift_start === v.shift_end) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["shift_end"], message: m.shiftEmpty });
		});
}
export const patchDriverBody = makePatchDriverRequest();
export type PatchDriverBody = z.input<typeof patchDriverBody>;

export interface Driver extends DriverRef {
	cluster: ClusterRef;
	provider: TransportProviderRef | null;
	vehicle_types: VehicleType[];
	/** `HH:MM`, games time zone. */
	shift_start: string;
	shift_end: string;
	/** End ≤ start. */
	overnight: boolean;
	rest_isodow: number | null;
	usual_vehicles: Array<Pick<VehicleRef, "vehicle_id" | "vehicle_type" | "plate">>;
	is_active: boolean;
	status_now: DriverNowStatus;
	trips_today: number;
	upcoming_trips: number;
	version: number;
	created_at: string;
	updated_at: string;
}

/** `GET /transport/drivers/:id/week?start=` — `start` is a Monday (games time zone; omitted = this week). */
export function makeDriverWeekQuery(m: FM = enTransportFleetFormMessages) {
	return z.object({ start: dateField(m).optional() });
}
export const driverWeekQuery = makeDriverWeekQuery();
export type DriverWeekQuery = z.output<typeof driverWeekQuery>;

export interface DriverWeekTrip {
	transport_allocation_id: string;
	transport_request_id: string;
	ref: string;
	source: TransportRequestSource;
	/** The local date it falls on (games time zone): the day card. */
	local_date: string;
	pickup_at: string;
	busy_from: string;
	busy_to: string;
	from: TransportPlaceRef;
	to: TransportPlaceRef;
	plate: string;
	/** The allocation's vehicle's type (the day card's icon). */
	vehicle_type: VehicleType;
	pax: number;
	organization_code: string | null;
	state: TransportAllocationState;
	status: TransportDisplayStatus;
}

/** At most TRANSPORT_DRIVER_WEEK_LIMIT trips. */
export interface DriverWeek {
	driver: Driver;
	start: string;
	end: string;
	timezone: string;
	trips: DriverWeekTrip[];
}

// ── Providers (SUPER_ADMIN writes) ──────────────────────────────────────────

/** Trimmed, ≤ max, then a valid address or `""` (= none). */
const emailField = (m: FM) =>
	z
		.string()
		.trim()
		.max(TRANSPORT_EMAIL_MAX, m.textTooLong)
		.refine((v) => v === "" || z.string().email().safeParse(v).success, m.emailInvalid);

export const listTransportProvidersQuery = z.object({
	...pageParams,
	search: searchText.optional(),
	is_active: booleanParam.optional(),
	sort: z.enum(["name", "created_at"]).optional(),
	dir: dirParam.optional(),
});
export type ListTransportProvidersQuery = z.output<typeof listTransportProvidersQuery>;

export function makeCreateTransportProviderRequest(m: FM = enTransportFleetFormMessages) {
	return z.object({
		name: z.string().trim().min(1, m.nameRequired).max(TRANSPORT_NAME_MAX, m.textTooLong),
		contact_name: nullableText(m, TRANSPORT_NAME_MAX),
		contact_phone: nullableText(m, TRANSPORT_PHONE_MAX),
		email: emailField(m)
			.nullable()
			.optional()
			.transform((v) => (v ? v : null)),
		is_active: z.boolean().default(true),
	});
}
export const createTransportProviderBody = makeCreateTransportProviderRequest();
export type CreateTransportProviderBody = z.input<typeof createTransportProviderBody>;

export function makePatchTransportProviderRequest(m: FM = enTransportFleetFormMessages) {
	return z
		.object({
			name: z.string().trim().min(1, m.nameRequired).max(TRANSPORT_NAME_MAX, m.textTooLong).optional(),
			contact_name: patchText(m, TRANSPORT_NAME_MAX),
			contact_phone: patchText(m, TRANSPORT_PHONE_MAX),
			email: emailField(m)
				.transform((v) => (v === "" ? null : v))
				.nullable()
				.optional(),
			is_active: z.boolean().optional(),
			reason: z.string().trim().min(1).max(TRANSPORT_REASON_MAX, m.textTooLong).optional(),
		})
		.refine(({ reason: _r, ...rest }) => Object.values(rest).some((x) => x !== undefined), { message: m.nothingToChange });
}
export const patchTransportProviderBody = makePatchTransportProviderRequest();
export type PatchTransportProviderBody = z.input<typeof patchTransportProviderBody>;

/** `GET /transport/providers/options` — a plain array of the active providers, for pickers (vehicle / driver sheets, the provider group's facility editor). Admins and dispatch desks. */
export type TransportProviderOption = TransportProviderRef;

export interface TransportProvider extends TransportProviderRef {
	contact_name: string | null;
	contact_phone: string | null;
	email: string | null;
	is_active: boolean;
	vehicle_count: number;
	driver_count: number;
	/** Provider groups naming it (facility rows). */
	group_count: number;
	created_at: string;
	updated_at: string;
}

// ── Clusters and drive minutes ──────────────────────────────────────────────

export const CLUSTER_SORT_KEYS = ["sort_order", "code", "name", "created_at"] as const;
export type ClusterSortKey = (typeof CLUSTER_SORT_KEYS)[number];

export const listClustersQuery = z.object({
	...pageParams,
	search: searchText.optional(),
	sort: z.enum(CLUSTER_SORT_KEYS).optional(),
	dir: dirParam.optional(),
});
export type ListClustersQuery = z.output<typeof listClustersQuery>;

const clusterCode = (m: FM) => z.string().trim().toUpperCase().min(1, m.codeInvalid).max(CLUSTER_CODE_MAX, m.codeInvalid).regex(CLUSTER_CODE_PATTERN, m.codeInvalid);
const sortOrderField = (m: FM) => z.number({ invalid_type_error: m.sortOrderInvalid }).int(m.sortOrderInvalid).min(0, m.sortOrderInvalid).max(CLUSTER_SORT_ORDER_MAX, m.sortOrderInvalid);

/** `POST /clusters` (shared by both games; transport, then lodging and dining). Code and name unique (409 CONFLICT `{ field }`). */
export function makeCreateClusterRequest(m: FM = enTransportFleetFormMessages) {
	return z.object({
		code: clusterCode(m),
		name: z.string().trim().min(1, m.nameRequired).max(CLUSTER_NAME_MAX, m.textTooLong),
		remarks: nullableText(m, TRANSPORT_REMARKS_MAX),
		sort_order: sortOrderField(m).default(0),
	});
}
export const createClusterBody = makeCreateClusterRequest();
export type CreateClusterBody = z.input<typeof createClusterBody>;

/** `PATCH /clusters/:id`. A delete while anything uses it → 409 CONFLICT (`rethrowInUse`). */
export function makePatchClusterRequest(m: FM = enTransportFleetFormMessages) {
	return z
		.object({
			code: clusterCode(m).optional(),
			name: z.string().trim().min(1, m.nameRequired).max(CLUSTER_NAME_MAX, m.textTooLong).optional(),
			remarks: patchText(m, TRANSPORT_REMARKS_MAX),
			sort_order: sortOrderField(m).optional(),
			reason: z.string().trim().min(1).max(TRANSPORT_REASON_MAX, m.textTooLong).optional(),
		})
		.refine(({ reason: _r, ...rest }) => Object.values(rest).some((x) => x !== undefined), { message: m.nothingToChange });
}
export const patchClusterBody = makePatchClusterRequest();
export type PatchClusterBody = z.input<typeof patchClusterBody>;

export interface Cluster extends ClusterRef {
	remarks: string | null;
	sort_order: number;
	venue_count: number;
	entry_point_count: number;
	place_count: number;
	vehicle_count: number;
	driver_count: number;
	created_at: string;
	updated_at: string;
}

/** `GET /clusters/options` — any GMS role, for pickers. */
export type ClusterOption = ClusterRef;

/** One directed pair of `cluster_drive_minutes`. A missing pair falls back to `transport_settings.default_drive_minutes`. */
export interface ClusterDriveMinutes {
	from_cluster_id: string;
	to_cluster_id: string;
	minutes: number;
}

/** `PUT /clusters/drive-minutes` — replace-set of the matrix (one DELETE + one INSERT … SELECT). The UI writes both directions unless the user splits them. */
export function makeSetDriveMinutesRequest(m: FM = enTransportFleetFormMessages) {
	return z.object({
		rows: z
			.array(z.object({ from_cluster_id: uuid, to_cluster_id: uuid, minutes: driveMinutes(m) }))
			.max(CLUSTER_DRIVE_ROWS_MAX, m.rowsMax)
			.superRefine((rows, ctx) => {
				const seen = new Set<string>();
				rows.forEach((r, i) => {
					const key = `${r.from_cluster_id.toLowerCase()}|${r.to_cluster_id.toLowerCase()}`;
					// A cluster to itself is a real drive time (a trip inside KL): only a pair listed twice is refused.
					if (seen.has(key))
						ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i], message: m.pairInvalid });
					seen.add(key);
				});
			}),
	});
}
export const setDriveMinutesBody = makeSetDriveMinutesRequest();
export type SetDriveMinutesBody = z.input<typeof setDriveMinutesBody>;

export interface ClusterDriveMinutesResult {
	items: ClusterDriveMinutes[];
}

// ── Places, drop-offs, settings ─────────────────────────────────────────────

export const listTransportPlacesQuery = z.object({
	...pageParams,
	search: searchText.optional(),
	cluster_id: uuid.optional(),
	is_active: booleanParam.optional(),
	sort: z.enum(["name", "created_at"]).optional(),
	dir: dirParam.optional(),
});
export type ListTransportPlacesQuery = z.output<typeof listTransportPlacesQuery>;

/**
 * `POST /transport/places` — a hotel, embassy, training ground. Name unique,
 * case-insensitive. `lodging_building_id` (0034): the M11 building this place
 * stands for — travellers with a room there are driven to / from it — at most
 * one place per building (409 HOTEL_ALREADY_LINKED); null / omitted = none.
 */
export function makeCreateTransportPlaceRequest(m: FM = enTransportFleetFormMessages) {
	return z.object({
		name: z.string().trim().min(1, m.nameRequired).max(TRANSPORT_NAME_MAX, m.textTooLong),
		address: nullableText(m, TRANSPORT_ADDRESS_MAX),
		cluster_id: uuid,
		is_active: z.boolean().default(true),
		lodging_building_id: uuid.nullable().default(null),
	});
}
export const createTransportPlaceBody = makeCreateTransportPlaceRequest();
export type CreateTransportPlaceBody = z.input<typeof createTransportPlaceBody>;

export function makePatchTransportPlaceRequest(m: FM = enTransportFleetFormMessages) {
	return z
		.object({
			name: z.string().trim().min(1, m.nameRequired).max(TRANSPORT_NAME_MAX, m.textTooLong).optional(),
			address: patchText(m, TRANSPORT_ADDRESS_MAX),
			cluster_id: uuid.optional(),
			is_active: z.boolean().optional(),
			/** null unlinks the hotel; omitted leaves it. */
			lodging_building_id: uuid.nullable().optional(),
			reason: z.string().trim().min(1).max(TRANSPORT_REASON_MAX, m.textTooLong).optional(),
		})
		.refine(({ reason: _r, ...rest }) => Object.values(rest).some((x) => x !== undefined), { message: m.nothingToChange });
}
export const patchTransportPlaceBody = makePatchTransportPlaceRequest();
export type PatchTransportPlaceBody = z.input<typeof patchTransportPlaceBody>;

/** The lodging building (M11) a transport place stands for. */
export interface TransportPlaceHotelRef {
	lodging_building_id: string;
	name: string;
}

export interface TransportPlace {
	transport_place_id: string;
	name: string;
	address: string | null;
	cluster: ClusterRef;
	is_active: boolean;
	/**
	 * The hotel this place stands for (0034), null = none: a traveller with an
	 * open room there is driven to it (arrival) / from it (departure) by
	 * suggested transfers, while the place is active; else the contingent's
	 * drop-off.
	 */
	lodging_building: TransportPlaceHotelRef | null;
	/** Requests that start or end here: a delete is refused (409 CONFLICT) while > 0. */
	trip_count: number;
	created_at: string;
	updated_at: string;
}

/**
 * `GET /transport/places/options?games_id=` — a plain array of every place a
 * trip may use: venues only if they have a cluster, entry points, active
 * transport places. Any login that may request or dispatch. `own_dropoff`
 * marks the caller's contingent's default drop-off in that games (a NOC
 * pre-selects it).
 */
export const transportPlaceOptionsQuery = z.object({ games_id: uuid });

/**
 * `GET /transport/places/hotel-options` — the place form's Hotel picker: every
 * lodging building (≤ LODGING_BUILDINGS_LIMIT, the accommodation list's
 * order) with the place already standing for it, if any. The fleet desk only.
 */
export interface TransportPlaceHotelOption extends TransportPlaceHotelRef {
	type: LodgingBuildingType;
	is_active: boolean;
	/** The building's cluster: the form presets the place's cluster from it. */
	cluster_id: string;
	/** The place linked to it already (null = free). */
	place: { transport_place_id: string; name: string } | null;
}
export interface TransportPlaceOption extends TransportPlaceRef {
	own_dropoff: boolean;
}
export type TransportPlaceOptions = TransportPlaceOption[];

/** One contingent's default drop-off (X5). */
export interface TransportDropoff {
	responsible_organization: AccreditationOrgRef;
	place: TransportPlaceRef;
	updated_at: string;
}

export const transportDropoffsQuery = z.object({ games_id: uuid });
export interface TransportDropoffsResult {
	items: TransportDropoff[];
}

/** `PUT /transport/dropoffs` — replace-set for one games: a venue or a transport place per contingent. */
export function makeSetDropoffsRequest(m: FM = enTransportFleetFormMessages) {
	return z.object({
		games_id: uuid,
		rows: z
			.array(z.object({ responsible_organization_id: uuid, place: z.object({ kind: z.enum(DROPOFF_PLACE_KINDS), id: uuid }) }))
			.max(TRANSPORT_DROPOFFS_MAX, m.rowsMax)
			.refine((rows) => uniqueIds(rows.map((r) => r.responsible_organization_id)), m.idsDuplicate),
	});
}
export const setDropoffsBody = makeSetDropoffsRequest();
export type SetDropoffsBody = z.input<typeof setDropoffsBody>;

/** `GET /transport/settings/:games_id`. */
export interface TransportSettings {
	games_id: string;
	default_drive_minutes: number;
	noc_lead_hours: number;
	adhoc_buffer_minutes: number;
	group_spread_minutes: number;
	updated_at: string;
}

const settingField = (m: FM, key: TransportSettingKey) =>
	z
		.number({ invalid_type_error: m.numberInvalid })
		.int(m.numberInvalid)
		.min(TRANSPORT_SETTINGS_RANGES[key].min, m.numberInvalid)
		.max(TRANSPORT_SETTINGS_RANGES[key].max, m.numberInvalid);

/** `PATCH /transport/settings/:games_id` — any subset; ranges from TRANSPORT_SETTINGS_RANGES (the DB CHECKs too). */
export function makePatchTransportSettingsRequest(m: FM = enTransportFleetFormMessages) {
	return z
		.object({
			default_drive_minutes: settingField(m, "default_drive_minutes").optional(),
			noc_lead_hours: settingField(m, "noc_lead_hours").optional(),
			adhoc_buffer_minutes: settingField(m, "adhoc_buffer_minutes").optional(),
			group_spread_minutes: settingField(m, "group_spread_minutes").optional(),
			reason: z.string().trim().min(1).max(TRANSPORT_REASON_MAX, m.textTooLong).optional(),
		})
		.refine(({ reason: _r, ...rest }) => Object.values(rest).some((x) => x !== undefined), { message: m.nothingToChange });
}
export const patchTransportSettingsBody = makePatchTransportSettingsRequest();
export type PatchTransportSettingsBody = z.input<typeof patchTransportSettingsBody>;

// ── S3 additions (requests & dispatch API; appended) ────────────────────────

/** Needs attention OVERDUE: PENDING trips whose pickup passed at most this long ago. */
export const TRANSPORT_OVERDUE_LOOKBACK_HOURS = 24;
/** Needs attention NO_PASSENGERS / RAMP_NEEDED look this far ahead of now. */
export const TRANSPORT_ATTENTION_HORIZON_HOURS = 72;
/**
 * A trip with a vehicle on the road (EN_ROUTE, or PARTIAL) stays in the
 * Upcoming tab and counts as "on the road" while its pickup is at most this
 * far in the past; an older one (a forgotten Arrived) falls back to Past.
 */
export const TRANSPORT_ON_ROAD_LOOKBACK_HOURS = 48;

/**
 * The time window each Needs-attention kind is counted in, relative to now
 * (hours; `null` = unbounded on that side). The summary's count and the list's
 * `attention=` filter use the same predicate, window included.
 *   REDISPATCH         OPEN, `needs_redispatch`, any time
 *   OVERDUE            PENDING, pickup in [now − 24 h, now)
 *   RECHECK            OPEN, `needs_recheck`, pickup ≥ now
 *   FLIGHT_CANCELLED   OPEN, pickup ≥ now, a live passenger's flight CANCELLED
 *   NO_PASSENGERS      OPEN, pickup in [now, now + 72 h), nobody named or counted
 *   RAMP_NEEDED        PENDING, pickup in [now, now + 72 h), a wheelchair user
 *   MAINTENANCE_BOOKED OPEN, an ASSIGNED allocation still ahead on a MAINTENANCE vehicle
 */
export const TRANSPORT_ATTENTION_WINDOWS = {
	REDISPATCH: { from: null, to: null },
	OVERDUE: { from: -TRANSPORT_OVERDUE_LOOKBACK_HOURS, to: 0 },
	RECHECK: { from: 0, to: null },
	FLIGHT_CANCELLED: { from: 0, to: null },
	NO_PASSENGERS: { from: 0, to: TRANSPORT_ATTENTION_HORIZON_HOURS },
	RAMP_NEEDED: { from: 0, to: TRANSPORT_ATTENTION_HORIZON_HOURS },
	MAINTENANCE_BOOKED: { from: null, to: null },
} as const satisfies Record<TransportAttentionKind, { from: number | null; to: number | null }>;

/**
 * `GET /transport/summary` for a requester without dispatch (a NOC, §0b Q3):
 * built in SQL from the caller's OWN trips and OWN passengers only — no
 * seats, no total passengers, no SHORT (a partly assigned trip shows as
 * ASSIGNED), no suggestions, re-dispatch / re-check internals or fleet.
 * Admins, dispatch desks and providers get `TransportSummary` (no `view`).
 */
export interface TransportNocSummary {
	view: "noc";
	date: string;
	timezone: string;
	/** The caller's trips picking up on `date`, by the status it sees (SHORT folded into ASSIGNED): every other TRANSPORT_DISPLAY_STATUSES member, in order. */
	by_status: TransportStatusCount<Exclude<TransportDisplayStatus, "SHORT">>[];
	/**
	 * Its trips on the list's Upcoming tab (pickup from now on, plus those still
	 * on the road), by the same status: each count = that list's total with
	 * `status=` set, their sum = the unfiltered Upcoming total. Same members, in order.
	 */
	upcoming_by_status: TransportStatusCount<Exclude<TransportDisplayStatus, "SHORT">>[];
	/** Its trips picking up in the next 24 h. */
	next_24h: number;
	/** Its trips with a vehicle on the road now. */
	on_the_road: number;
	/** Its own people on `date`'s trips: the named passengers it can see, plus the unnamed count of its own trips. */
	own_passengers: number;
}

/** What `GET /transport/summary` answers: `"view" in s && s.view === "noc"` tells the NOC shape apart. */
export type TransportSummaryResult = TransportSummary | TransportNocSummary;

/** `POST /transport/allocations/:id/{start,arrive,cancel}` — the allocation after the step. */
export interface TransportAllocationStepResult {
	transport_allocation_id: string;
	transport_request_id: string;
	ref: string;
	state: TransportAllocationState;
	cancel_reason: TransportAllocationCancelReason | null;
	started_at: string | null;
	arrived_at: string | null;
}

/**
 * (E2E B3) A service a transfer follows that has no `flights` row — a coach,
 * a ferry, a flight typed by hand — named as its legs name it. A transfer
 * whose `follows_travel` is true follows `flights` and these. The times are
 * the legs': ARR the latest, DEP the earliest; `expected_at` later than
 * `scheduled_at` = a manual delay ("Follows Coach SG-1 (+2 h)").
 */
export interface TransportServiceRef {
	service_no: string;
	scheduled_at: string | null;
	expected_at: string | null;
}

/**
 * (E2E B6) `GET /transport/requests/:id/activity` — ONE item per write (the
 * audit rows of one API request, or of one transaction with one actor and
 * reason when there is none: the tracker, the cascade, a seed), newest first,
 * keyset on (the write's latest time, its group id). The desk's view only.
 */
export interface TransportActivityItem {
	/** The write's headline audit row: the request's own row when the write touched it, else an allocation's, else a passenger's. */
	audit_log_id: string;
	/** The write's latest audit time. */
	occurred_at: string;
	actor: { type: "USER" | "AI_AGENT" | "SYSTEM" | "MIGRATION"; user_id: string | null; username: string | null; name: string | null; label: string | null };
	reason: string | null;
	/** What the write did to the request row itself; null when it touched only passengers / allocations. */
	action: "INSERT" | "UPDATE" | "DELETE" | null;
	/** The request's changed columns (bookkeeping excluded), and each one's first `from` and last `to` within the write. */
	changed_fields: string[];
	changes: Record<string, { from: unknown; to: unknown }>;
	/** Passenger rows of the write: added (new or back), released (dropped, kept as history), removed (detached: rebooked, card moved). */
	passengers: { added: number; released: number; removed: number };
	/** Allocation rows of the write: added, cancelled (→ CANCELLED, any reason), changed (vehicle / driver / pax, Start, Arrived). */
	allocations: { added: number; cancelled: number; changed: number };
}

export interface TransportActivityPage {
	items: TransportActivityItem[];
	next_cursor: string | null;
}
